import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, AlertCircle, AlertTriangle, Award, BookOpen, Clock, FileText, ArrowLeft, RefreshCw, Layers, Sparkles, Loader2, Save, Info, Check, PlusCircle, PenTool } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { WritingV2CorrectionPanel } from "@/components/writing/WritingV2CorrectionPanel";
import { JustifiedQuestionText } from "@/components/JustifiedQuestionText";
import { formatPercentage } from "@/lib/utils";
import { 
  getCompetencyLabel, 
  getTefLevelByScore, 
  getTefScore, 
  getCECRL, 
  getNCLC, 
  getScoreGapToGoal, 
  getUserGoals,
  getTargetScoreForNclc,
  type GoalGap
} from "@/data/tefScoreScale";
import { normalizeWritingCorrection } from "@/utils/writingCorrectionUtils";
import { mockWritingCorrection } from "@/data/mockWritingCorrection";
import WritingCorrectionReport from "@/components/writing/WritingCorrectionReport";
import { requestWritingCorrection, fetchAIUsage, type CorrectionSource } from "@/services/writingCorrectionService";
import { getOrCreateClientId } from "@/utils/clientId";
import { historyService } from "@/services/historyService";
import { errorBankService } from "@/services/errorBankService";
import { analyzeAnswerReview, generatePedagogicalRecommendations, getQuestionStudyActionPayload } from "@/utils/resultAnalysisUtils";
import { flashcardService } from "@/services/flashcardService";
import type { WritingTaskV2, WritingCorrectionResultV2 } from "@/types/writingV2";
import { correctWritingSectionAV2, correctWritingSectionBV2 } from "@/services/writingV2CorrectionService";
import { isWritingV2Result, isLegacyWritingResult } from "@/utils/writingV2Helpers";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";


function resolveWritingV2TasksFromState(state: any, examResult?: any) {
  if (Array.isArray(state?.writingTasks) && state.writingTasks.length > 0) {
    return state.writingTasks;
  }

  if (state?.writingTask) {
    return [state.writingTask];
  }

  if (state?.historyRecord?.metadata?.writingTask) {
    return [state.historyRecord.metadata.writingTask];
  }

  if (Array.isArray(state?.historyRecord?.metadata?.writingTasks)) {
    return state.historyRecord.metadata.writingTasks;
  }

  if (examResult?.metadata?.writingTask) {
    return [examResult.metadata.writingTask];
  }

  if (Array.isArray(examResult?.metadata?.writingTasks) && examResult.metadata.writingTasks.length > 0) {
    return examResult.metadata.writingTasks;
  }

  if (examResult?.metadata?.fullOriginalText || examResult?.text) {
    return [{
      id: "legacy_writing_task",
      section: examResult?.section || "Expression Écrite",
      userText: examResult?.metadata?.fullOriginalText || examResult?.text || "",
      wordCount: examResult?.words || 0
    }];
  }

  return [];
}

function isWritingEE(input: any): boolean {
  if (!input) return false;
  
  if (input.resultType === "writing_v2" || input.writingVersion === 2 || input.metadata?.writingVersion === 2) {
    return true;
  }

  const comp = String(input.competency || input.competence || input.resultType || "").toLowerCase();
  const section = String(input.section || "").toLowerCase();

  if (
    comp.includes("expression écrite") || 
    comp.includes("expression ecrite") ||
    comp.includes("writing") ||
    comp === "ee"
  ) {
    return true;
  }

  if (section.includes("comprehension écrite") || section.includes("comprehension ecrite") || section.includes("ce")) {
    return false;
  }

  if (section.includes("expression écrite") || section.includes("expression ecrite") || section.includes("ee")) {
    return true;
  }

  return false;
}

function getWritingErrorKey(error: any, index?: number): string {
  return (
    error.id ||
    `${error.section || "section"}-${error.category || "category"}-${error.original || "original"}-${index ?? 0}`
  );
}

const getQuestionPrompt = (q: any) => {
  if (!q) return "";
  return q.question || q.prompt || q.statement || q.enonce || q.enoncé || q.command || q.task || q.metadata?.question || q.metadata?.prompt || "";
};

function withTimeout<T>(promise: Promise<T>, ms: number, label = "Operação") {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} excedeu o tempo limite de ${ms / 1000}s.`));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function getSkillResult(result: any, skill: "reading" | "listening" | "writing") {
  if (!result) return null;

  let skillData: any = null;

  if (skill === "reading") {
    skillData = 
      result.reading || 
      result.ce || 
      result.comprehensionEcrite || 
      result.sections?.reading || 
      result.details?.reading ||
      result.result?.ce ||
      result.result?.reading ||
      result.result?.sections?.reading;
  } else if (skill === "listening") {
    skillData = 
      result.listening || 
      result.co || 
      result.comprehensionOrale || 
      result.sections?.listening || 
      result.details?.listening ||
      result.result?.co ||
      result.result?.listening ||
      result.result?.sections?.listening;
  } else if (skill === "writing") {
    skillData = 
      result.writing || 
      result.ee || 
      result.expressionEcrite || 
      result.sections?.writing || 
      result.details?.writing ||
      result.result?.ee ||
      result.result?.writing ||
      result.result?.sections?.writing;
  }

  if (!skillData) return null;

  const correct = 
    skillData.correctAnswers !== undefined ? skillData.correctAnswers : 
    skillData.correct !== undefined ? skillData.correct : 
    skillData.score !== undefined ? skillData.score : 0;

  const total = 
    skillData.totalQuestions !== undefined ? skillData.totalQuestions : 
    skillData.total !== undefined ? skillData.total : 
    skillData.maxScore !== undefined ? skillData.maxScore : 0;

  const percent = 
    skillData.percentage !== undefined ? skillData.percentage : 
    skillData.percent !== undefined ? skillData.percent : 
    (total > 0 ? Math.round((correct / total) * 100) : 0);

  const nclc = 
    skillData.nclcLevel || 
    skillData.nclc || 
    (skill !== "writing" && total > 0 ? getNCLC(skill, correct, total) : null);

  const cefr = 
    skillData.cefrLevel || 
    skillData.cefr || 
    skillData.cefrEstimate || 
    skillData.cecrlLevel || 
    skillData.cecrl || 
    (skill !== "writing" && total > 0 ? getCECRL(skill, correct, total) : null);

  return {
    correct,
    total,
    percent,
    nclc,
    cefr,
    text: skillData.text || "",
    words: skillData.words || skillData.wordCount || 0,
    correction: skillData.correction || null,
    status: skillData.status || "pending",
    answerReview: skillData.answerReview || []
  };
}

function getPerformanceMessage(correctAnswers: number, totalQuestions: number, t?: any): string {
  if (!totalQuestions || totalQuestions <= 0) {
    return t ? t("testResult.unanswered") : "Sem questões avaliadas.";
  }

  const percentage = (correctAnswers / totalQuestions) * 100;

  if (percentage === 100) return t ? t("testResult.performancePerfect") : "Desempenho perfeito!";
  if (percentage >= 80) return t ? t("testResult.performanceGreat") : "Ótimo desempenho!";
  if (percentage >= 60) return t ? t("testResult.performanceGood") : "Bom desempenho.";
  if (percentage >= 40) return t ? t("testResult.performanceAverage") : "Desempenho intermediário.";
  if (percentage > 0) return t ? t("testResult.performanceKeepPracticing") : "Continue praticando.";
  return t ? t("testResult.performanceNone") : "Nenhum acerto nesta seção.";
}

function isWritingResult(record: any, state: any): boolean {
  if (state?.resultType === "writing_v2" || state?.writingVersion === 2) return true;
  if (record?.resultType === "writing_v2" || record?.metadata?.writingVersion === 2) return true;

  const stateType = String(state?.resultType || "").toLowerCase();
  const stateCompetency = String(state?.competency || "").toLowerCase();

  const competency = String(
    record?.competency ??
    record?.competence ??
    record?.skill ??
    ""
  ).toLowerCase();

  const section = String(
    record?.section ??
    record?.task ??
    ""
  ).toLowerCase();

  // Safeguard: strictly block reading or listening from writing results
  if (
    stateType.includes("reading") || stateType.includes("listening") ||
    stateCompetency.includes("reading") || stateCompetency.includes("listening") ||
    competency.includes("reading") || competency.includes("listening") ||
    competency.includes("comprehension")
  ) {
    return false;
  }

  return (
    stateType === "writing" ||
    stateCompetency === "ee" ||
    competency.includes("ee") ||
    competency.includes("expression écrite") ||
    competency.includes("expression ecrite") ||
    competency.includes("writing") ||
    (
      (section.includes("section a") || section.includes("section b")) &&
      !section.includes("comprehension")
    )
  );
}

function getWritingNclcFromScore(score: number) {
  if (score >= 393) return { nclc: "10+", cefr: "C1/C2" };
  if (score >= 371) return { nclc: "9", cefr: "C1" };
  if (score >= 349) return { nclc: "8", cefr: "B2" };
  if (score >= 310) return { nclc: "7", cefr: "B2" };
  if (score >= 271) return { nclc: "6", cefr: "B1" };
  if (score >= 226) return { nclc: "5", cefr: "B1" };
  if (score >= 181) return { nclc: "4", cefr: "A2" };
  return { nclc: "<4", cefr: "A1" };
}

function resolveWritingLevel(score: any, record: any, correction: any) {
  const directNclc =
    record?.nclc_level ||
    record?.nclc ||
    record?.metadata?.nclc ||
    correction?.nclc ||
    correction?.nclcLevel;

  const directCefr =
    record?.cefr_level ||
    record?.cefr ||
    record?.metadata?.cefr ||
    correction?.cefr ||
    correction?.cefrLevel ||
    correction?.cecrl;

  if (directNclc && directCefr) {
    return { nclc: directNclc, cefr: directCefr };
  }

  const numericScore = typeof score === "number" ? score : parseFloat(score);
  if (!Number.isNaN(numericScore)) {
    return getWritingNclcFromScore(numericScore);
  }

  return { nclc: "N/A", cefr: "N/A" };
}

function normalizeAnswer(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toUpperCase();
}

function getOptionText(options: any, answer: unknown): string {
  const normalized = normalizeAnswer(answer);
  if (!normalized) return "";
  if (!options) return "";

  if (Array.isArray(options)) {
    const found = options.find((opt, index) => {
      const letter = String.fromCharCode(65 + index);
      return normalizeAnswer(opt.id ?? opt.key ?? opt.letter ?? letter) === normalized;
    });

    if (!found) return "";
    if (typeof found === "string") return found;
    return found.text ?? found.label ?? found.content ?? "";
  }

  if (typeof options === "object") {
    return options[normalized] ?? options[normalized.toLowerCase()] ?? "";
  }

  return "";
}


function buildAnswerReview(currentQuestions: any[], answers: Record<string, any>) {
  return (currentQuestions || []).map((question, index) => {
    const questionId = question.id;

    const userAnswerRaw =
      answers?.[questionId] ??
      answers?.[String(questionId)] ??
      answers?.[index] ??
      answers?.[String(index)] ??
      null;

    const correctAnswerRaw =
      question.correctAnswer ??
      question.correct_answer ??
      question.correct_option ??
      question.correctOption ??
      question.answer ??
      question.gabarito ??
      question.metadata?.correctAnswer ??
      question.metadata?.correct_answer ??
      null;

    const userAnswer = normalizeAnswer(userAnswerRaw);
    const correctAnswer = normalizeAnswer(correctAnswerRaw);

    const isAnswered = userAnswer.length > 0;
    const isCorrect =
      isAnswered &&
      correctAnswer.length > 0 &&
      userAnswer === correctAnswer;

    return {
      questionId,
      questionNumber: index + 1,
      title: question.title || `Questão ${index + 1}`,
      instruction: question.instruction || question.command || "",
      question: getQuestionPrompt(question),
      text: question.text || question.content || "",
      userAnswer: isAnswered ? userAnswer : null,
      correctAnswer: correctAnswer || null,
      isAnswered,
      isCorrect,
      explanation:
        question.explanation ||
        question.feedback ||
        question.justification ||
        question.metadata?.explanation ||
        question.metadata?.feedback ||
        "",
      options:
        question.options ||
        question.alternatives ||
        question.choices ||
        question.metadata?.options ||
        [],
      competence: question.competence,
      section: question.section,
      subSection: question.subSection || question.sub_section || question.metadata?.subSection || question.metadata?.sub_section || undefined
    };
  });
}

function getAnswerReview(result: any, locationState: any, competence: string) {
  if (Array.isArray(locationState?.answerReview) && locationState.answerReview.length > 0) return locationState.answerReview;
  if (Array.isArray(result?.answerReview) && result.answerReview.length > 0) return result.answerReview;
  if (Array.isArray(locationState?.result?.answerReview) && locationState.result.answerReview.length > 0) return locationState.result.answerReview;

  const comp = String(competence || result?.competence || locationState?.competence || "").toLowerCase();

  if (comp.includes("reading") || comp.includes("ce") || comp.includes("compréhension écrite")) {
    const readingReview = result?.reading?.answerReview ?? result?.ce?.answerReview ?? result?.sections?.reading?.answerReview ?? result?.result?.ce?.answerReview ?? result?.result?.reading?.answerReview;
    if (Array.isArray(readingReview) && readingReview.length > 0) return readingReview;
  }

  if (comp.includes("listening") || comp.includes("co") || comp.includes("compréhension orale")) {
    const listeningReview = result?.listening?.answerReview ?? result?.co?.answerReview ?? result?.sections?.listening?.answerReview ?? result?.result?.co?.answerReview ?? result?.result?.listening?.answerReview;
    if (Array.isArray(listeningReview) && listeningReview.length > 0) return listeningReview;
  }

  if (Array.isArray(locationState?.questions) && locationState.questions.length > 0) {
    return buildAnswerReview(locationState.questions, locationState.answers || {});
  }

  return [];
}

const PedagogicalAnalysisPanel = ({ reviewItems, title, description, className = "" }: { reviewItems: any[]; title?: string; description?: string; className?: string }) => {
  const { t } = useTranslation();
  const displayTitle = title || t("testResult.pedagogicalAnalysisTitle");
  const displayDescription = description || t("testResult.pedagogicalAnalysisDesc");

  const getRecommendationLocalization = (recTitle: string, recDesc: string, analysis: any) => {
    if (recTitle.startsWith("Evite deixar questões em branco")) {
      return {
        title: t("testResult.recommendationBlankTitle"),
        description: t("testResult.recommendationBlankDesc", { count: analysis.unansweredCount })
      };
    }
    if (recTitle.startsWith("Reforçar esta competência")) {
      return {
        title: t("testResult.recommendationReinforceTitle"),
        description: t("testResult.recommendationReinforceDesc")
      };
    }
    if (recTitle.startsWith("Bom desempenho geral")) {
      return {
        title: t("testResult.recommendationGreatTitle"),
        description: t("testResult.recommendationGreatDesc")
      };
    }
    if (recTitle.startsWith("Priorize a seção")) {
      const sectionName = recTitle.replace("Priorize a seção: ", "");
      return {
        title: t("testResult.recommendationSectionTitle", { section: sectionName }),
        description: t("testResult.recommendationSectionDesc")
      };
    }
    if (recTitle.startsWith("Revise as questões incorretas")) {
      return {
        title: t("testResult.recommendationIncorrectTitle"),
        description: t("testResult.recommendationIncorrectDesc", { count: analysis.incorrectCount })
      };
    }
    if (recTitle.startsWith("Transforme erros em revisão")) {
      return {
        title: t("testResult.recommendationTransformTitle"),
        description: t("testResult.recommendationTransformDesc")
      };
    }
    return { title: recTitle, description: recDesc };
  };

  if (!reviewItems || reviewItems.length === 0) {
    return (
      <Card className={`border-none shadow-sm bg-white ${className}`}>
        <CardHeader>
          <CardTitle>{displayTitle}</CardTitle>
          <CardDescription>{displayDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-xl text-xs font-bold flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-slate-500 shrink-0" />
            <span>{t("testResult.noDataToAnalyze")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const analysis = analyzeAnswerReview(reviewItems);
  const recommendations = generatePedagogicalRecommendations(analysis);

  return (
    <Card className={`border-none shadow-sm bg-white overflow-hidden space-y-6 ${className}`}>
      <CardHeader className="border-b border-gray-50 pb-4">
        <CardTitle>{displayTitle}</CardTitle>
        <CardDescription>{displayDescription}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6 pt-2">
        {/* Cards Resumo */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-emerald-50/60 border border-emerald-100 p-3 rounded-2xl text-center space-y-1">
            <span className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider block">{t("testResult.correctCount")}</span>
            <span className="text-2xl font-black text-emerald-600 font-mono">{analysis.correctCount}</span>
          </div>
          <div className="bg-red-50/60 border border-red-100 p-3 rounded-2xl text-center space-y-1">
            <span className="text-[10px] text-red-800 font-bold uppercase tracking-wider block">{t("testResult.incorrectCount")}</span>
            <span className="text-2xl font-black text-red-600 font-mono">{analysis.incorrectCount}</span>
          </div>
          <div className="bg-amber-50/60 border border-amber-100 p-3 rounded-2xl text-center space-y-1">
            <span className="text-[10px] text-amber-800 font-bold uppercase tracking-wider block">{t("testResult.unansweredCount")}</span>
            <span className="text-2xl font-black text-amber-600 font-mono">{analysis.unansweredCount}</span>
          </div>
          <div className="bg-blue-50/60 border border-blue-100 p-3 rounded-2xl text-center space-y-1">
            <span className="text-[10px] text-blue-800 font-bold uppercase tracking-wider block">{t("testResult.performancePercent")}</span>
            <span className="text-2xl font-black text-blue-600 font-mono">{Math.round(analysis.accuracy)}%</span>
          </div>
        </div>

        {/* Desempenho por seção */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{t("testResult.performanceBySection")}</h4>
          <div className="space-y-3 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
            {analysis.bySection.map((sec, idx) => (
              <div key={idx} className="space-y-1.5">
                <div className="flex justify-between text-xs font-semibold text-gray-700">
                  <span className="font-bold">{sec.section}</span>
                  <span className="font-mono text-gray-500">
                    {sec.correct}/{sec.total} ({Math.round(sec.accuracy)}%)
                  </span>
                </div>
                <Progress 
                  value={sec.accuracy} 
                  className={`h-2 bg-gray-200 ${sec.accuracy < 50 ? '[&>div]:bg-red-500' : sec.accuracy < 80 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`} 
                />
              </div>
            ))}
          </div>
        </div>

        {/* Sugestões de estudo */}
        {recommendations.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{t("testResult.studySuggestions")}</h4>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {recommendations.map((rec, idx) => {
                const localized = getRecommendationLocalization(rec.title, rec.description, analysis);
                return (
                  <div key={idx} className={`p-3.5 rounded-2xl border space-y-1 ${rec.priority === 'high' ? 'bg-red-50/40 border-red-100' : rec.priority === 'medium' ? 'bg-amber-50/40 border-amber-100' : 'bg-emerald-50/40 border-emerald-100'}`}>
                    <div className="flex items-center gap-2">
                      <BookOpen className={`w-4 h-4 shrink-0 ${rec.priority === 'high' ? 'text-red-600' : rec.priority === 'medium' ? 'text-amber-600' : 'text-emerald-600'}`} />
                      <span className="text-xs font-black text-gray-900">{localized.title}</span>
                    </div>
                    <p className="text-[11px] text-gray-600 font-semibold leading-relaxed pl-6">{localized.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AnswerReviewPanel = ({ 
  reviewItems, 
  title, 
  description,
  compType = "reading",
  savedErrorIds = {},
  createdFlashcardIds = {},
  savingErrorId = null,
  creatingFlashcardId = null,
  onSaveError,
  onCreateFlashcard
}: { 
  reviewItems: any[]; 
  title?: string; 
  description?: string;
  compType?: string;
  savedErrorIds?: Record<string, boolean>;
  createdFlashcardIds?: Record<string, boolean>;
  savingErrorId?: string | null;
  creatingFlashcardId?: string | null;
  onSaveError?: (item: any, compType: string) => void;
  onCreateFlashcard?: (item: any, compType: string) => void;
}) => {
  const { t } = useTranslation();
  const displayTitle = title || t("testResult.answerReviewTitle");
  const displayDescription = description || t("testResult.answerReviewDesc");

  if (!reviewItems || reviewItems.length === 0) {
    return (
      <Card className="border-none shadow-sm bg-white">
        <CardHeader>
          <CardTitle>{displayTitle}</CardTitle>
          <CardDescription>{displayDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span>{t("testResult.cannotLoadReview")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalQuestions = reviewItems.length;
  const correctAnswers = reviewItems.filter(item => item.isCorrect).length;

  return (
    <Card className="border-none shadow-sm bg-white overflow-hidden">
      <CardHeader className="border-b border-gray-50 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <CardTitle>{displayTitle}</CardTitle>
          <CardDescription>{displayDescription}</CardDescription>
        </div>
        <div className="flex items-center gap-2 font-mono text-xs font-bold px-3 py-1.5 bg-gray-50 rounded-xl">
          <span className="text-gray-500">{t("testResult.correctAnswersCount")}</span>
          <span className="text-emerald-600">{correctAnswers} / {totalQuestions}</span>
          <span className="text-gray-400">({Math.round((correctAnswers / totalQuestions) * 100)}%)</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4 max-h-[600px] overflow-y-auto">
        {reviewItems.map((item, idx) => {
          const uOptText = getOptionText(item.options, item.userAnswer);
          const cOptText = getOptionText(item.options, item.correctAnswer);
          const userDisplay = item.userAnswer ? `${item.userAnswer}${uOptText ? ` - ${uOptText}` : ""}` : t("testResult.notAnswered");
          const correctDisplay = item.correctAnswer ? `${item.correctAnswer}${cOptText ? ` - ${cOptText}` : ""}` : "N/A";
          const itemKey = String(item.questionId || item.questionNumber);

          return (
            <div key={item.questionId || idx} className={`p-4 border rounded-2xl space-y-2.5 transition-all duration-200 ${item.isCorrect ? "bg-emerald-50/60 border-emerald-100/80" : "bg-red-50/60 border-red-100/80"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-bold text-sm text-gray-900 flex-wrap">
                  {item.isCorrect ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />}
                  <span>Questão {item.questionNumber}: {item.title}</span>
                  {item.subSection && (
                    <Badge variant="outline" className="text-[10px] bg-white text-purple-700 border-purple-200">
                      {item.subSection === "phrases_lacunaires" ? "Phrases lacunaires" :
                       item.subSection === "textes_lacunaires" ? "Textes lacunaires" :
                       item.subSection === "lecture_rapide_textes" ? "Lecture rapide (textos)" :
                       "Lecture rapide (gráfico)"}
                    </Badge>
                  )}
                </div>
                <Badge variant="outline" className={`text-[10px] font-extrabold ${item.isCorrect ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-red-100 text-red-800 border-red-200"}`}>
                  {item.isCorrect ? t("testResult.correct") : (item.userAnswer ? t("testResult.incorrect") : t("testResult.notAnswered"))}
                </Badge>
              </div>

              {item.instruction && (
                <p className="text-xs text-gray-500 font-semibold italic">{item.instruction}</p>
              )}

              {item.question && (
                <div className="bg-slate-900 text-white p-3.5 rounded-xl shadow-sm text-xs font-black animate-in slide-in-from-top-1 duration-300">
                  {item.question}
                </div>
              )}

              {item.text && (
                <JustifiedQuestionText text={item.text} className="text-xs text-gray-700 bg-white/80 p-2.5 rounded-xl border border-gray-100 font-serif" />
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                <div className="bg-white/60 p-2 rounded-xl space-y-0.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase block">{t("testResult.userAnswerLabel")}</span>
                  <span className={`font-mono font-bold ${item.isCorrect ? "text-emerald-700" : "text-red-700"}`}>{userDisplay}</span>
                </div>
                <div className="bg-white/60 p-2 rounded-xl space-y-0.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase block">{t("testResult.correctAnswerLabel")}</span>
                  <span className="font-mono font-bold text-gray-800">{correctDisplay}</span>
                </div>
              </div>

              {item.explanation && (
                <p className="text-xs text-gray-600 leading-relaxed pt-1 border-t border-gray-100/50">
                  <strong className="text-gray-700 font-bold">{t("testResult.explanation")}</strong> {item.explanation}
                </p>
              )}


            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const resolveCompetencyFromValue = (v: any): "reading" | "listening" | "writing" | "" => {
  if (!v) return "";
  const str = String(v).toLowerCase().trim();
  if (str.includes("listening") || str.includes("orale") || str === "co") {
    return "listening";
  }
  if (str.includes("compréhension écrite") || str.includes("reading") || str === "ce") {
    return "reading";
  }
  if (str.includes("expression écrite") || str.includes("writing") || str === "ee") {
    return "writing";
  }
  return "";
};

export default function TestResult() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "reading";
  const mode = searchParams.get("mode") || "";
  const id = searchParams.get("id") || "";

  const [examResult, setExamResult] = useState<any>(null);

  useEffect(() => {
    console.log("[TestResult] received state", location.state);
  }, [location]);
  const [userGoals] = useState(() => getUserGoals());
  
  const state = useMemo(() => {
    if (location.state) return location.state;
    try {
      const saved = sessionStorage.getItem("tef_test_result_fallback_state");
      if (saved) return JSON.parse(saved);
    } catch(e) {}
    return {};
  }, [location.state]);

  const [historyRecord, setHistoryRecord] = useState<any>(state?.historyRecord || null);

  console.log("[TestResult From History]", JSON.stringify({
    historyRecord,
    metadata: historyRecord?.metadata,
    writingCorrection: historyRecord?.metadata?.writingCorrection,
    writingTasks: historyRecord?.metadata?.writingTasks,
    sectionScores: historyRecord?.metadata?.sectionScores,
  }, null, 2));

  const fromHistory = state.fromHistory === true;
  const shouldRunWritingCorrection = state.shouldRunWritingCorrection;

  const isWritingV2 =
    state?.resultType === "writing_v2" ||
    state?.writingVersion === 2 ||
    state?.metadata?.writingVersion === 2 ||
    historyRecord?.resultType === "writing_v2" ||
    historyRecord?.metadata?.writingVersion === 2 ||
    historyRecord?.writingVersion === 2 ||
    examResult?.resultType === "writing_v2" ||
    examResult?.metadata?.writingVersion === 2 ||
    examResult?.writingVersion === 2;

  const isListening =
    state?.resultType === "listening" ||
    state?.testType === "listening" ||
    resolveCompetencyFromValue(state?.competency) === "listening" ||
    resolveCompetencyFromValue(state?.competence) === "listening" ||
    historyRecord?.resultType === "listening" ||
    resolveCompetencyFromValue(historyRecord?.competency) === "listening" ||
    resolveCompetencyFromValue(historyRecord?.competence) === "listening" ||
    examResult?.resultType === "listening" ||
    resolveCompetencyFromValue(examResult?.competency) === "listening" ||
    resolveCompetencyFromValue(examResult?.competence) === "listening" ||
    type === "listening";

  const isReading =
    state?.resultType === "reading" ||
    state?.testType === "reading" ||
    resolveCompetencyFromValue(state?.competency) === "reading" ||
    resolveCompetencyFromValue(state?.competence) === "reading" ||
    historyRecord?.resultType === "reading" ||
    resolveCompetencyFromValue(historyRecord?.competency) === "reading" ||
    resolveCompetencyFromValue(historyRecord?.competence) === "reading" ||
    examResult?.resultType === "reading" ||
    resolveCompetencyFromValue(examResult?.competency) === "reading" ||
    resolveCompetencyFromValue(examResult?.competence) === "reading" ||
    type === "reading";

  console.log("[TEST_RESULT_MODE_RESOLVED]", {
    resultType: state?.resultType || null,
    testType: state?.testType || null,
    competency: state?.competency || null,
    isWritingV2,
    isListening,
    isReading,
  });

  const isWritingReview = !isListening && !isReading && isWritingResult(historyRecord, state);

  useStudyTimeTracker({
    activityType: isWritingReview ? "writing_review" : "history_review",
    competency: isWritingReview ? "writing" : "general",
    enabled: true
  });


  const writingCorrection =
    state.writingCorrection ||
    historyRecord?.metadata?.writingCorrection ||
    null;

  const writingTasks =
    state.writingTasks ||
    (state.writingTask ? [state.writingTask] : null) ||
    historyRecord?.metadata?.writingTasks ||
    [];

  console.log("[TEST_RESULT_EE_INPUT_STATE]", JSON.stringify({
    isWritingResult: isWritingReview,
    shouldRunWritingCorrection,
    writingTasks,
    originalWritingText: state.originalWritingText,
    totalWordCount: state.totalWordCount,
  }, null, 2));
  const isFromHistory = Boolean(state.fromHistory);
  const isWritingResultFlag = !isListening && !isReading && (
    state.resultType === "writing" ||
    state.competency === "EE" ||
    state.competence === "writing" ||
    String(state.competency || "").toLowerCase().includes("expression écrite") ||
    String(state.competency || "").toLowerCase().includes("expression ecrite")
  );

  const hasWritingCorrection = Boolean(
    state.writingCorrection ||
    historyRecord?.metadata?.writingCorrection
  );

  const originalTextValue =
    examResult?.metadata?.fullOriginalText ||
    examResult?.metadata?.originalWritingText ||
    examResult?.metadata?.userText ||
    examResult?.metadata?.originalAnswer ||
    examResult?.text ||
    writingCorrection?.originalText ||
    writingCorrection?.userText ||
    "";

  const taskTextsValue = (examResult?.metadata?.writingTasks || [])
    .map((task: any) => task.userText || task.answer || task.text || "")
    .filter(Boolean);

  const displayOriginalTextValue =
    originalTextValue || taskTextsValue.join("\n\n") || decodeURIComponent(searchParams.get("text") || "");

  const hasOriginalText = String(displayOriginalTextValue || "").trim().length > 20;

  console.log("[EE_TEST_RESULT_MODE_DECISION]", {
    isFromHistory,
    isWritingResult: isWritingResultFlag,
    hasWritingCorrection,
    hasOriginalText,
    originalTextLength: displayOriginalTextValue?.length,
    shouldRunAI:
      isWritingResultFlag &&
      !isFromHistory &&
      !hasWritingCorrection &&
      hasOriginalText,
  });

  const shouldRunWritingAI =
    isWritingResultFlag &&
    !isFromHistory &&
    !hasWritingCorrection &&
    hasOriginalText;

  const sectionScores =
    state.sectionScores ||
    historyRecord?.metadata?.sectionScores ||
    null;

  const writingScore =
    Number(
      state.score ??
      historyRecord?.score ??
      historyRecord?.metadata?.totalScore ??
      writingCorrection?.totalScore ??
      writingCorrection?.score ??
      0
    ) || 0;

  const writingMaxScore =
    Number(
      state.maxScore ??
      historyRecord?.max_score ??
      historyRecord?.metadata?.maxScore ??
      writingCorrection?.maxScore ??
      450
    ) || 450;

  console.log("[TEST_RESULT_EE_REVIEW_DATA]", JSON.stringify({
    isWritingReview,
    historyRecord,
    writingCorrection,
    writingTasks,
    sectionScores,
    writingScore,
    writingMaxScore,
  }, null, 2));

  const isWritingFromHistory = isWritingReview;

  const writingLevel = resolveWritingLevel(writingScore, historyRecord, writingCorrection);

  // AI Writing Correction states
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [correctionResult, setCorrectionResult] = useState<any>(state?.writingCorrection || null);
  
  const [isSavingHistory, setIsSavingHistory] = useState(false);
  const [historySaveStatus, setHistorySaveStatus] = useState<"idle" | "success" | "error">("idle");

  async function handleManualSaveToHistory() {
    const realCorrection = correctionResult || writingCorrection;

    console.log("[EE_MANUAL_SAVE_CLICK]", JSON.stringify(realCorrection, null, 2));

    if (!realCorrection) {
      alert(t("testResult.cannotFindCorrectionData", { defaultValue: "Não foi possível encontrar os dados da correção." }));
      return;
    }

    try {
      setIsSavingHistory(true);

      const saved = await historyService.saveWritingCorrectionAsNewHistoryRecord({
        correctionResult: realCorrection,
        writingTasks: writingTasks || [],
        section: "Completo",
        timeUsed: typeof timeSpent === "number" ? timeSpent : null,
        wordCount: typeof words === "number" ? words : null,
      });

      setHistorySaveStatus("success");

      console.log("[EE_MANUAL_SAVE_DONE]", JSON.stringify(saved, null, 2));

      alert(t("testResult.historySaveSuccess", { defaultValue: "Resultado salvo no histórico com sucesso." }));
    } catch (error) {
      setHistorySaveStatus("error");
      console.error("[EE_MANUAL_SAVE_FAILED]", error);
      alert(t("testResult.historySaveError", { defaultValue: "Não foi possível salvar o resultado no histórico. Veja o console." }));
    } finally {
      setIsSavingHistory(false);
    }
  }
  
  useEffect(() => {
    if (isWritingFromHistory || type === "writing") {
      console.log("[TestResult EE State Correction]", {
        stateWritingCorrection: state?.writingCorrection,
        stateWritingTasks: state?.writingTasks,
        stateSectionScores: state?.sectionScores,
      });
    }
  }, [isWritingFromHistory, type, state]);

  useEffect(() => {
    async function tryPersistMissingCorrection() {
      if (!isWritingFromHistory) return;
      
      const record = historyRecord || examResult;
      if (!record) return;

      const stateCorrection = state?.writingCorrection;
      
      if (record && !record.metadata?.writingCorrection && stateCorrection) {
        console.log("[TestResult] Trying to persist missing correction from state to Supabase");
        try {
          const { historyService } = await import("@/services/historyService");
          await historyService.persistWritingCorrection({
            historyId: record.id,
            correctionResult: stateCorrection,
            writingTasks: state?.writingTasks || [],
            section: record.section || "Section B",
            competency: "EE",
            existingMetadata: record.metadata,
          });
          console.log("[TestResult] Successfully persisted missing correction to Supabase");
        } catch (err) {
          console.error("[TestResult] Failed to persist missing correction to Supabase", err);
        }
      }
    }

    tryPersistMissingCorrection();
  }, [isWritingFromHistory, historyRecord, examResult, state]);
  const [correctionSource, setCorrectionSource] = useState<CorrectionSource | null>(null);
  const [correctionWarning, setCorrectionWarning] = useState<string | null>(null);
  const [localToast, setLocalToast] = useState<string | null>(null);
  const [showFullCorrectionModal, setShowFullCorrectionModal] = useState(false);


  // Ações de Estudo states
  const [savedErrorIds, setSavedErrorIds] = useState<Record<string, boolean>>({});
  const [createdFlashcardIds, setCreatedFlashcardIds] = useState<Record<string, boolean>>({});
  const [savingErrorId, setSavingErrorId] = useState<string | null>(null);
  const [creatingFlashcardId, setCreatingFlashcardId] = useState<string | null>(null);

  const handleSaveError = async (item: any, compType: string) => {
    const itemKey = String(item.questionId || item.questionNumber);
    if (savedErrorIds[itemKey] || savingErrorId === itemKey) return;

    setSavingErrorId(itemKey);
    try {
      const { errorPayload } = getQuestionStudyActionPayload(item, getOptionText, compType, id);
      await errorBankService.createError(errorPayload as any);
      setSavedErrorIds(prev => ({ ...prev, [itemKey]: true }));
      showLocalToast("✨ Erro salvo no Banco de Erros.");
    } catch (err: any) {
      if (err.message && (err.message.includes("já está cadastrado") || err.message.includes("já existe"))) {
        setSavedErrorIds(prev => ({ ...prev, [itemKey]: true }));
        showLocalToast("✨ Este item já estava salvo.");
      } else {
        showLocalToast(`⚠️ Erro ao salvar: ${err.message}`);
      }
    } finally {
      setSavingErrorId(null);
    }
  };

  const handleCreateFlashcard = async (item: any, compType: string) => {
    const itemKey = String(item.questionId || item.questionNumber);
    if (createdFlashcardIds[itemKey] || creatingFlashcardId === itemKey) return;

    setCreatingFlashcardId(itemKey);
    try {
      const { flashcardPayload } = getQuestionStudyActionPayload(item, getOptionText, compType, id);
      await flashcardService.createFlashcard(flashcardPayload as any);
      setCreatedFlashcardIds(prev => ({ ...prev, [itemKey]: true }));
      showLocalToast("✨ Flashcard criado.");
    } catch (err: any) {
      if (err.message && (err.message.includes("já existe") || err.message.includes("já está cadastrado"))) {
        setCreatedFlashcardIds(prev => ({ ...prev, [itemKey]: true }));
        showLocalToast("✨ Este item já estava salvo.");
      } else {
        showLocalToast(`⚠️ Erro ao criar flashcard: ${err.message}`);
      }
    } finally {
      setCreatingFlashcardId(null);
    }
  };

  // AI usage states
  const [usageInfo, setUsageInfo] = useState<{ usedToday: number; dailyLimit: number; remaining: number } | null>(null);

  useEffect(() => {
    const loadUsage = async () => {
      const clientId = getOrCreateClientId();
      const res = await fetchAIUsage(clientId);
      if (res.success) {
        setUsageInfo({
          usedToday: res.usedToday,
          dailyLimit: res.dailyLimit,
          remaining: res.remaining
        });
      } else {
        setUsageInfo(null);
      }
    };
    loadUsage();
  }, []);

  const showLocalToast = (msg: string) => {
    setLocalToast(msg);
    setTimeout(() => {
      setLocalToast(null);
    }, 4000); // 4 seconds for better visibility of warnings
  };

  const handleStartAiCorrection = async (
    userText: string, 
    targetNclc: string | number, 
    sectionName = "Section B", 
    instructionText = "Sujet de rédaction d'Expression Écrite TEF Canada."
  ) => {
    if (isCorrecting) return; // Prevent duplicate clicks

    // Free Trial Limits Check (3 corrections limit)
    if (profile && profile.role !== "admin" && profile.plan !== "premium") {
      try {
        const historyList = await historyService.getTestHistory();
        const eeCorrectionsCount = historyList.filter(h => h.competence === "Expression Écrite (EE)").length;
        if (eeCorrectionsCount >= 3) {
          alert(t("testResult.writingLimitReached", "Limite atingido! Você já utilizou o limite de 3 correções de redação por IA no plano Trial. Faça o upgrade para o Premium para ter correções ilimitadas!"));
          navigate("/premium");
          return;
        }
      } catch (err) {
        console.warn("Failed to check corrections limits:", err);
      }
    }

    if (!userText || userText.trim().length < 20) {
      showLocalToast("⚠️ Digite ou produza seu texto antes de solicitar a correção.");
      return;
    }

    console.log("[EE_ORIGINAL_TEXT_BEFORE_AI]", {
      section: sectionName,
      textLength: userText?.length,
      wordCount: userText?.trim()?.split(/\s+/).filter(Boolean).length,
      originalText: userText,
    });

    console.log("[EE_AI_CORRECTION_REQUEST]", {
      section: sectionName,
      textLength: userText?.length,
      originalText: userText,
    });

    setIsCorrecting(true);
    try {
      const clientId = getOrCreateClientId();
      const response = await requestWritingCorrection({
        section: sectionName,
        instruction: instructionText,
        userText: userText,
        targetNclc: targetNclc,
        clientId: clientId,
        historyId: id || historyRecord?.id || examResult?.id || state?.historyId,
        writingTasks: writingTasks || examResult?.metadata?.writingTasks || [],
        existingMetadata: historyRecord?.metadata || examResult?.metadata
      });

      // Se o limite foi atingido, tratamos explicitamente sem disparar simulação/fallback
      if (!response.success && response.code === "AI_DAILY_LIMIT_REACHED") {
        setIsCorrecting(false);
        showLocalToast("⚠️ Limite atingido! Tente novamente amanhã.");
        setUsageInfo(prev => prev ? { ...prev, usedToday: prev.dailyLimit, remaining: 0 } : null);
        return;
      }

      setIsCorrecting(false);
      if (response.success && response.correction) {
        const enrichedCorrection: any = {
          ...response.correction,
          originalText: userText,
          instruction: instructionText,
          targetNclc: String(targetNclc),
          source: response.source || "openai",
          disclaimer: "Esta correção é uma estimativa pedagógica gerada por IA."
        };
        
        console.log("[EE_AI_CORRECTION_SUCCESS]", JSON.stringify(enrichedCorrection, null, 2));
        
        setCorrectionResult(enrichedCorrection);
        setCorrectionSource(response.source || "openai");
        setCorrectionWarning(response.warning || null);

        if (response.persistedHistoryRecord) {
          console.log("[TestResult] Using persisted record from service", response.persistedHistoryRecord.id);
          setHistoryRecord(response.persistedHistoryRecord);
          setExamResult(response.persistedHistoryRecord);
          if (response.persistedHistoryRecord.metadata?.writingCorrection) {
            setCorrectionResult(response.persistedHistoryRecord.metadata.writingCorrection);
          }
        }

        if (response.source === "frontend_fallback") {
          showLocalToast("⚠️ Servidor de correção offline. Usando fallback local!");
        } else if (response.source === "mock_fallback") {
          showLocalToast("⚠️ IA indisponível. Usando correção simulada!");
        } else if (response.source === "gemini") {
          showLocalToast("✨ Correção gerada com sucesso via Gemini!");
        } else if (response.source === "openai") {
          showLocalToast("✨ Correção gerada com sucesso via OpenAI!");
        } else {
          showLocalToast("✨ Correção gerada com sucesso via API local!");
        }

        // Formulate updated results and persist to DB
        let updatedResult: any = null;
        if (mode === "full_exam") {
          updatedResult = {
            ...examResult,
            score: `CE: ${examResult?.sections?.reading?.percent || 0}% | CO: ${examResult?.sections?.listening?.percent || 0}% | EE: ${enrichedCorrection.nclcEstimate || enrichedCorrection.nclcLevel || "B2"}`,
            sections: {
              ...examResult?.sections,
              writing: {
                ...examResult?.sections?.writing,
                correction: enrichedCorrection
              }
            },
            writingCorrection: enrichedCorrection,
            aiCorrection: enrichedCorrection,
            eeScore: enrichedCorrection.estimatedScore || enrichedCorrection.score,
            eeLevel: enrichedCorrection.nclcEstimate || enrichedCorrection.nclcLevel,
            correction: enrichedCorrection,
            criteria: enrichedCorrection.criteria,
            generalFeedback: enrichedCorrection.overallComment || enrichedCorrection.generalFeedback,
            errors: enrichedCorrection.errors || [],
            metadata: {
              ...(examResult?.metadata || {}),
              hasAiCorrection: true,
              correctionSource: response.source || "gemini",
              correctedAt: new Date().toISOString()
            }
          };
        } else {
          updatedResult = {
            ...examResult,
            score: `${enrichedCorrection.estimatedScore}/${enrichedCorrection.maxScore}`,
            percentage: Math.round(((enrichedCorrection.estimatedScore || 0) / (enrichedCorrection.maxScore || 450)) * 100),
            status: "Corrigido com IA",
            result: {
              score: enrichedCorrection.estimatedScore,
              maxScore: enrichedCorrection.maxScore,
              nclcLevel: enrichedCorrection.nclcEstimate,
              cefrLevel: enrichedCorrection.cecrlEstimate,
              correction: enrichedCorrection,
              criteria: enrichedCorrection.criteria,
              generalFeedback: enrichedCorrection.overallComment,
              source: enrichedCorrection.source,
              words: examResult?.words || 0,
              text: examResult?.text || "",
              timeUsed: examResult?.timeUsed || 0
            },
            writingCorrection: enrichedCorrection,
            aiCorrection: enrichedCorrection,
            eeScore: enrichedCorrection.estimatedScore,
            eeLevel: enrichedCorrection.nclcEstimate,
            correction: enrichedCorrection,
            criteria: enrichedCorrection.criteria,
            generalFeedback: enrichedCorrection.overallComment,
            errors: enrichedCorrection.errors || [],
            metadata: {
              ...(examResult?.metadata || {}),
              hasAiCorrection: true,
              correctionSource: response.source || "gemini",
              correctedAt: new Date().toISOString()
            }
          };

          // If it's a multi-task EE test, save correction into the specific section
          const isSectionA = sectionName === "Section A";
          const isSectionB = sectionName === "Section B";
          const currentAnswers = updatedResult.answers || updatedResult.result?.ee?.answers || {};
          
          if (currentAnswers && (isSectionA || isSectionB)) {
            const key = isSectionA ? "sectionA" : "sectionB";
            if (currentAnswers[key]) {
              console.log("[EE_CORRECTION_RECEIVED]", {
                correctionResult,
                score: correctionResult?.score,
                totalScore: correctionResult?.totalScore,
                nclc: correctionResult?.nclc,
                cefr: correctionResult?.cefr,
              });
      
              const innerCorr = {
                ...currentAnswers[key],
                correction: enrichedCorrection
              };
              updatedResult.answers = {
                ...currentAnswers,
                [key]: innerCorr
              };
            }
          }
        }

        setExamResult(updatedResult);



        // Atualiza as cotas de uso após o sucesso/fallback
        const updatedUsage = await fetchAIUsage(clientId);
        if (updatedUsage.success) {
          setUsageInfo({
            usedToday: updatedUsage.usedToday,
            dailyLimit: updatedUsage.dailyLimit,
            remaining: updatedUsage.remaining
          });
        }
      } else {
        throw new Error(response.message || "Erro desconhecido na resposta");
      }
    } catch (err: any) {
      console.error(err);
      setIsCorrecting(false);
      
      const enrichedCorrection: any = {
        ...mockWritingCorrection,
        originalText: userText,
        instruction: instructionText,
        targetNclc: String(targetNclc),
        source: "frontend_fallback",
        disclaimer: "Esta correção é uma estimativa pedagógica gerada por IA."
      };

      setCorrectionResult(enrichedCorrection);
      setCorrectionSource("frontend_fallback");
      setCorrectionWarning("Servidor de correção offline. Usando fallback local.");
      showLocalToast("⚠️ Erro de conexão. Ativando correção simulada local.");

      // Formulate updated results and persist to DB under fallback
      let updatedResult: any = null;
      if (mode === "full_exam") {
        updatedResult = {
          ...examResult,
          score: `CE: ${examResult?.sections?.reading?.percent || 0}% | CO: ${examResult?.sections?.listening?.percent || 0}% | EE: ${enrichedCorrection.nclcEstimate || enrichedCorrection.nclcLevel || "B2"}`,
          sections: {
            ...examResult?.sections,
            writing: {
              ...examResult?.sections?.writing,
              correction: enrichedCorrection
            }
          },
          writingCorrection: enrichedCorrection,
          aiCorrection: enrichedCorrection,
          eeScore: enrichedCorrection.estimatedScore || enrichedCorrection.score,
          eeLevel: enrichedCorrection.nclcEstimate || enrichedCorrection.nclcLevel,
          correction: enrichedCorrection,
          criteria: enrichedCorrection.criteria,
          generalFeedback: enrichedCorrection.overallComment || enrichedCorrection.generalFeedback,
          errors: enrichedCorrection.errors || [],
          metadata: {
            ...(examResult?.metadata || {}),
            hasAiCorrection: true,
            correctionSource: "frontend_fallback",
            correctedAt: new Date().toISOString()
          }
        };
      } else {
        updatedResult = {
          ...examResult,
          score: `${enrichedCorrection.estimatedScore}/${enrichedCorrection.maxScore}`,
          percentage: Math.round(((enrichedCorrection.estimatedScore || 0) / (enrichedCorrection.maxScore || 450)) * 100),
          status: "Corrigido com IA",
          result: {
            score: enrichedCorrection.estimatedScore,
            maxScore: enrichedCorrection.maxScore,
            nclcLevel: enrichedCorrection.nclcEstimate,
            cefrLevel: enrichedCorrection.cecrlEstimate,
            correction: enrichedCorrection,
            criteria: enrichedCorrection.criteria,
            generalFeedback: enrichedCorrection.overallComment,
            source: enrichedCorrection.source,
            words: examResult?.words || words,
            text: examResult?.text || text,
            timeUsed: examResult?.timeUsed || timeSpent
          },
          writingCorrection: enrichedCorrection,
          aiCorrection: enrichedCorrection,
          eeScore: enrichedCorrection.estimatedScore,
          eeLevel: enrichedCorrection.nclcEstimate,
          correction: enrichedCorrection,
          criteria: enrichedCorrection.criteria,
          generalFeedback: enrichedCorrection.overallComment,
          errors: enrichedCorrection.errors || [],
          metadata: {
            ...(examResult?.metadata || {}),
            hasAiCorrection: true,
            correctionSource: "frontend_fallback",
            correctedAt: new Date().toISOString()
          }
        };
      }

      setExamResult(updatedResult);

      let targetId = id;
      if (!targetId && examResult?.metadata?.testAttemptId) {
        try {
          const historyList = await historyService.getTestHistory();
          const match = historyList.find(h => h.metadata?.testAttemptId === examResult.metadata.testAttemptId);
          if (match) targetId = match.id;
        } catch (e) {
          console.error("Error looking up target historical row:", e);
        }
      }

      try {
        await historyService.createOrUpdateTestHistory(updatedResult);
        console.log("[TestResult] Persisted AI correction to database successfully");
      } catch (dbErr) {
        console.error("Failed to update test history in database:", dbErr);
      }
    }
  };

  const WritingCorrectionLoading = () => (
    <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-pulse">
      <div className="p-1.5 bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600" />
      <CardContent className="py-12 flex flex-col items-center justify-center text-center space-y-6">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
        <div className="space-y-2 max-w-md">
          <h3 className="text-base font-black text-slate-800">Corrigindo redação com IA...</h3>
          <p className="text-xs text-slate-500 leading-normal">
            Analisando estrutura, gramática, vocabulário e adequação ao TEF Canada.
          </p>
        </div>
        <div className="w-full max-w-xs bg-slate-100 rounded-xl p-4 border border-slate-100 text-left space-y-2 text-[11px] font-semibold text-slate-600">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-ping" />
            <span>Cumprimento da tarefa (Adequação)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping" />
            <span>Coerência e organização lógica</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" />
            <span>Sintaxe, tempos verbais e gramática</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping" />
            <span>Riqueza e precisão do vocabulário</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-ping" />
            <span>Identificação de erros reincidentes</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  useEffect(() => {
    if (id) {
      historyService.getTestHistoryById(id).then(found => {
        if (found) {
          setExamResult(found);
          // Pre-load AI writing corrections from database if present
          const rawItem = found as any;
          const corr =
            rawItem.writingCorrection ||
            rawItem.aiCorrection ||
            rawItem.correction ||
            rawItem.metadata?.writingCorrection ||
            rawItem.result?.ee?.correction ||
            rawItem.result?.writingCorrection ||
            rawItem.result?.correction ||
            rawItem.sections?.writing?.correction;
          
          if (corr) {
            const innerCorr = corr.sectionB || corr.sectionA || corr.correction || corr;
            setCorrectionResult(innerCorr);
            setCorrectionSource(innerCorr.source || rawItem.source || rawItem.metadata?.correctionSource || "gemini");
          }

          console.log("[TestResult Fetch History By ID]", id);
          console.log("[TestResult Fetched History Record]", rawItem);
        }
      }).catch(err => {
        console.error("Error fetching test result by ID:", err);
      });
    }
  }, [id]);

  useEffect(() => {
    if (examResult) {
      if (isWritingReview || isWritingResult(examResult, state)) {
        console.log("[TEST_RESULT_EE_SKIP_OBJECTIVE_REVIEW]");
        console.log("[TestResult] writing result", getSkillResult(examResult, "writing"));
        return;
      }

      console.log("[TestResult] reading result", getSkillResult(examResult, "reading"));
      console.log("[TestResult] listening result", getSkillResult(examResult, "listening"));
      console.log("[TestResult] writing result", getSkillResult(examResult, "writing"));
      console.log("[TestResult] answerReview loaded CE", getAnswerReview(examResult, state, "reading"));
      console.log("[TestResult] answerReview loaded CO", getAnswerReview(examResult, state, "listening"));
    }
  }, [examResult, state, isWritingFromHistory]);

  // MCQ specific parameters
  const score = parseInt(searchParams.get("score") || "0");
  const total = parseInt(searchParams.get("total") || "1");
  const percent = total > 0 ? (score / total) * 100 : 0;

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const correctLength = total > 0 ? (score / total) * circumference : 0;
  const incorrectLength = total > 0 ? ((total - score) / total) * circumference : 0;
  const singleAnswerReview = getAnswerReview(examResult, state, type === "reading" ? "reading" : "listening");

  const hasStartedWritingCorrectionRef = useRef(false);
  const [isCorrectingWriting, setIsCorrectingWriting] = useState(false);
  const [writingCorrectionError, setWritingCorrectionError] = useState<any>(null);
  const [stateWritingTasks, setStateWritingTasks] = useState<any[]>(writingTasks);
  const [hasAttemptedWritingCorrection, setHasAttemptedWritingCorrection] = useState(false);

  const initialCorrection = state?.writingCorrection || null;
  const initialSection = state.section || "Section A";

  const [writingV2CorrectionsBySection, setWritingV2CorrectionsBySection] = useState<Record<string, WritingCorrectionResultV2 | null>>(() => {
    const fromHistory = state?.fromHistory;
    const resultType = state?.resultType;
    const correction = state?.writingCorrection;
    
    if (fromHistory && resultType === "writing_v2" && correction) {
      const section = correction.section || "Section A";
      return {
        "Section A": section === "Section A" ? correction : null,
        "Section B": section === "Section B" ? correction : null,
      };
    }
    
    return {
      "Section A": initialSection === "Section A" ? initialCorrection : null,
      "Section B": initialSection === "Section B" ? initialCorrection : null
    };
  });
  const [isCorrectingBySection, setIsCorrectingBySection] = useState<Record<string, boolean>>({
    "Section A": false,
    "Section B": false
  });
  const [writingV2ErrorsBySection, setWritingV2ErrorsBySection] = useState<Record<string, string | null>>({
    "Section A": null,
    "Section B": null
  });
  const [savedRecordsBySection, setSavedRecordsBySection] = useState<Record<string, any>>({
    "Section A": null,
    "Section B": null
  });
  const [saveStatusBySection, setSaveStatusBySection] = useState<Record<string, "idle" | "success" | "error">>({
    "Section A": "idle",
    "Section B": "idle"
  });
  const [isSavingBySection, setIsSavingBySection] = useState<Record<string, boolean>>({
    "Section A": false,
    "Section B": false
  });

  const [isSendingErrorsToBankBySection, setIsSendingErrorsToBankBySection] = useState<Record<string, boolean>>({
    "Section A": false,
    "Section B": false
  });
  const [sentToErrorBankBySection, setSentToErrorBankBySection] = useState<Record<string, boolean>>({
    "Section A": false,
    "Section B": false
  });
  const [sendErrorsToBankErrorBySection, setSendErrorsToBankErrorBySection] = useState<Record<string, string | null>>({
    "Section A": null,
    "Section B": null
  });

  const [isCreatingFlashcard, setIsCreatingFlashcard] = useState<Record<string, boolean>>({});
  const [createdFlashcardsByErrorId, setCreatedFlashcardsByErrorId] = useState<Record<string, boolean>>({});
  const [isCreatingAllFlashcardsBySection, setIsCreatingAllFlashcardsBySection] = useState<Record<string, boolean>>({
    "Section A": false,
    "Section B": false
  });

  const handleCorrectWritingV2BySection = async (task: WritingTaskV2, section: string) => {
    console.log("[EE_V2_COMPLETE_CORRECT_SECTION_CLICK]", { section, task });
    
    // Free Trial Limits Check (3 corrections limit)
    if (profile && profile.role !== "admin" && profile.plan !== "premium") {
      try {
        const historyList = await historyService.getTestHistory();
        const eeCorrectionsCount = historyList.filter(h => h.competence === "Expression Écrite (EE)").length;
        if (eeCorrectionsCount >= 3) {
          alert(t("testResult.writingLimitReached", "Limite atingido! Você já utilizou o limite de 3 correções de redação por IA no plano Trial. Faça o upgrade para o Premium para ter correções ilimitadas!"));
          navigate("/premium");
          return;
        }
      } catch (err) {
        console.warn("Failed to check corrections limits:", err);
      }
    }

    setIsCorrectingBySection(prev => ({ ...prev, [section]: true }));
    setWritingV2ErrorsBySection(prev => ({ ...prev, [section]: null }));
    
    try {
      let result;
      if (section === "Section A") {
        result = await correctWritingSectionAV2(task);
      } else if (section === "Section B") {
        result = await correctWritingSectionBV2(task);
      } else {
        throw new Error("Seção não suportada para correção pedagógica.");
      }

      console.log("[EE_V2_COMPLETE_CORRECT_SECTION_SUCCESS]", { section, result });
      
      setWritingV2CorrectionsBySection(prev => ({ ...prev, [section]: result }));
    } catch (error: any) {
      console.error("[EE_V2_COMPLETE_CORRECT_SECTION_ERROR]", { section, error });
      
      console.log("[EE_V2_CORRECTION_ERROR_PRESERVE_TASK]", {
        section: task?.section,
        userTextLength: task?.userText?.length || 0,
        wordCount: task?.wordCount,
        hasPreviousCorrection: Boolean(writingV2CorrectionsBySection?.[task?.section]),
      });

      setWritingV2ErrorsBySection(prev => ({ ...prev, [section]: error.message || "Erro desconhecido ao corrigir." }));
    } finally {
      setIsCorrectingBySection(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleSaveWritingV2HistoryBySection = async (task: any, correction: any, section: string) => {
    try {
      setIsSavingBySection(prev => ({ ...prev, [section]: true }));
      
      console.log("[EE_V2_SAVE_HISTORY_CLICK]", { task, correction, section });

      const saved = await historyService.saveWritingV2CorrectionToHistory({
        task,
        correction,
        testMode: state.testType === "writing_v2_complete" ? "complete" : "single",
      });

      setSavedRecordsBySection(prev => ({ ...prev, [section]: saved }));
      setSaveStatusBySection(prev => ({ ...prev, [section]: "success" }));
    } catch (error: any) {
      console.error("[EE_V2_SAVE_HISTORY_FAILED]", error);
      setSaveStatusBySection(prev => ({ ...prev, [section]: "error" }));
    } finally {
      setIsSavingBySection(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleSendWritingV2ErrorsToBankBySection = async (task: any, correction: any, section: string) => {
    try {
      setIsSendingErrorsToBankBySection(prev => ({ ...prev, [section]: true }));
      setSendErrorsToBankErrorBySection(prev => ({ ...prev, [section]: null }));

      const historyRecordId = savedRecordsBySection[section]?.id || historyRecord?.id || null;
      
      console.log("[EE_V2_SEND_ERRORS_TO_BANK_CLICK]", {
        task,
        correction,
        historyRecordId,
        section
      });

      await errorBankService.saveWritingV2ErrorsToErrorBank({
        task,
        correction,
        historyRecordId,
      });

      setSentToErrorBankBySection(prev => ({ ...prev, [section]: true }));
    } catch (error: any) {
      console.error("[EE_V2_SEND_ERRORS_TO_BANK_FAILED]", error);
      setSendErrorsToBankErrorBySection(prev => ({ ...prev, [section]: error.message || "Não foi possível enviar os erros." }));
    } finally {
      setIsSendingErrorsToBankBySection(prev => ({ ...prev, [section]: false }));
    }
  };

  // Keep old handlers as proxies for individual mode
  const handleCorrectWritingV2 = async (task: WritingTaskV2) => {
    await handleCorrectWritingV2BySection(task, task.section);
  };

  const handleSaveWritingV2History = async () => {
    const task = writingTasks[0];
    const correction = writingV2CorrectionsBySection[task.section] || initialCorrection;
    await handleSaveWritingV2HistoryBySection(task, correction, task.section);
  };

  const handleSendWritingV2ErrorsToBank = async () => {
    const task = writingTasks[0];
    const correction = writingV2CorrectionsBySection[task.section] || initialCorrection;
    await handleSendWritingV2ErrorsToBankBySection(task, correction, task.section);
  };



  const handleCreateFlashcardFromError = async (error: any, task: any, section: string) => {
    const errorKey = getWritingErrorKey(error);
    try {
      setIsCreatingFlashcard(prev => ({ ...prev, [errorKey]: true }));

      const historyRecordId = savedRecordsBySection[section]?.id || historyRecord?.id || null;

      console.log("[EE_V2_SINGLE_FLASHCARD_CLICK]", {
        errorId: error.id,
        section: section,
        category: error.category,
        severity: error.severity,
        original: error.original,
        corrected: error.corrected,
        hasExplanation: Boolean(error.explanation),
        hasContext: Boolean(error.contextSentence),
        historyRecordId,
      });

      const created = await flashcardService.createFlashcardsFromWritingV2Errors({
        errors: [error],
        task,
        historyRecordId,
      });

      console.log("[EE_V2_SINGLE_FLASHCARD_SUCCESS]", {
        errorId: error.id,
        created,
      });

      setCreatedFlashcardsByErrorId(prev => ({ ...prev, [errorKey]: true }));
    } catch (err: any) {
      console.error("[EE_V2_SINGLE_FLASHCARD_FAILED]", err);
    } finally {
      setIsCreatingFlashcard(prev => ({ ...prev, [errorKey]: false }));
    }
  };

  const handleCreateFlashcardsFromErrors = async (task: any, correction: any, section: string) => {
    try {
      setIsCreatingAllFlashcardsBySection(prev => ({ ...prev, [section]: true }));

      const uncreatedErrors = (correction?.errors || []).filter((error: any) => {
        const errorId = error.id || `${error.original}-${error.corrected}`;
        return !createdFlashcardsByErrorId[errorId];
      });

      console.log("[EE_V2_CREATE_ALL_FLASHCARDS_CLICK]", {
        uncreatedCount: uncreatedErrors.length,
        task,
        section,
      });

      if (uncreatedErrors.length === 0) {
        return;
      }

      const result = await flashcardService.createFlashcardsFromWritingV2Errors({
        errors: uncreatedErrors,
        task,
        historyRecordId: savedRecordsBySection[section]?.id || historyRecord?.id || null,
      });

      console.log("[EE_V2_CREATE_ALL_FLASHCARDS_SUCCESS]", result);

      const newCreated: Record<string, boolean> = { ...createdFlashcardsByErrorId };
      uncreatedErrors.forEach((error: any) => {
        const errorId = error.id || `${error.original}-${error.corrected}`;
        newCreated[errorId] = true;
      });
      setCreatedFlashcardsByErrorId(newCreated);
    } catch (err: any) {
      console.error("[EE_V2_CREATE_ALL_FLASHCARDS_FAILED]", err);
    } finally {
      setIsCreatingAllFlashcardsBySection(prev => ({ ...prev, [section]: false }));
    }
  };

  const runWritingCorrection = useCallback(async () => {
    if (!shouldRunWritingAI) return;
    if (hasStartedWritingCorrectionRef.current) return;

    console.log("[EE_CORRECTION_RUN_BEGIN]", {
      shouldRunWritingAI,
      originalTextLength: displayOriginalTextValue?.length,
      hasOriginalText: String(displayOriginalTextValue || "").trim().length > 20,
      writingTasksCount: writingTasks?.length || 0,
    });

      const tasksWithText = writingTasks.filter((task: any) =>
        String(task.userText || task.answer || task.text || "").trim().length > 0
      );

      const tasksToCorrect = tasksWithText.length > 0 
        ? tasksWithText 
        : [
            {
              id: "fallback-task",
              section: state.section || "Completo",
              title: "Redação",
              prompt: "Sujet de rédaction d'Expression Écrite TEF Canada.",
              userText: displayOriginalTextValue,
              answer: displayOriginalTextValue,
              text: displayOriginalTextValue,
              wordCount: displayOriginalTextValue.trim().split(/\s+/).filter(Boolean).length,
            }
          ];

      hasStartedWritingCorrectionRef.current = true;

      console.log("[EE_CORRECTION_START_WITH_ORIGINAL_TEXT]", {
        originalTextLength: displayOriginalTextValue.length,
        wordCount: displayOriginalTextValue.trim().split(/\s+/).filter(Boolean).length,
        writingTasks: tasksToCorrect,
      });

      setHasAttemptedWritingCorrection(true);
      setIsCorrectingWriting(true);

      try {

        const corrections: Record<string, any> = {};
        let totalScore = 0;
        let totalMaxScore = 0;
        let nclcLevel = "N/A";
        let cefrLevel = "N/A";
        let criteria: any[] = [];
        let generalFeedback = "";
        let sourceUsed = "local";

        for (let idx = 0; idx < tasksToCorrect.length; idx++) {
          const task = tasksToCorrect[idx];
          const isSecB = task.sectionKey === "section_b" || String(task.section || "").toLowerCase().includes("b");
          const label = isSecB ? "Section B" : "Section A";

          const payload = {
            section: label,
            instruction: task.prompt || task.instruction || "",
            userText: task.userText || task.answer || "",
            clientId: getOrCreateClientId(),
            historyId: id || historyRecord?.id || examResult?.id || state.historyId,
            writingTasks: tasksToCorrect.map((t: any) => ({ id: t.id, title: t.title || "", instruction: t.prompt || t.instruction || "" })),
            existingMetadata: historyRecord?.metadata || examResult?.metadata
          };

          const res = await requestWritingCorrection(payload);
          console.log("[EE_CORRECTION_SUCCESS_WITH_ORIGINAL_TEXT] partial", JSON.stringify(res, null, 2));

          if (res.success && res.correction) {
            corrections[isSecB ? "sectionB" : "sectionA"] = {
              ...res.correction,
              originalText: task.userText || task.answer || "",
              instruction: task.prompt || task.instruction || ""
            };
            totalScore += res.correction.estimatedScore || 0;
            totalMaxScore += res.correction.maxScore || 450;
            nclcLevel = res.correction.nclcEstimate || nclcLevel;
            cefrLevel = res.correction.cecrlEstimate || cefrLevel;
            const criterionArray = res.correction.criteria 
              ? Object.values(res.correction.criteria) 
              : [];
            criteria = [...criteria, ...criterionArray];
            generalFeedback += `${label}:\n${res.correction.overallComment || ""}\n\n`;
            sourceUsed = res.source || "gemini";
          } else {
            throw new Error(res.message || `Erro ao corrigir a ${label}`);
          }
        }

        const combinedCorrection = {
          score: totalScore,
          maxScore: totalMaxScore,
          nclcLevel,
          cefrLevel,
          correction: corrections,
          criteria,
          generalFeedback: generalFeedback.trim(),
          source: sourceUsed
        };

        console.log("[EE_CORRECTION_SUCCESS_WITH_ORIGINAL_TEXT]", JSON.stringify(combinedCorrection, null, 2));

        console.log("[EE_AI_CALL_SUCCESS]", JSON.stringify(combinedCorrection, null, 2));
        console.log("[EE_CORRECTION_SUCCESS_WITH_ORIGINAL_TEXT]", JSON.stringify(combinedCorrection, null, 2));

        setCorrectionResult(combinedCorrection);
        setStateWritingTasks(tasksToCorrect);
      } catch (error: any) {
        console.error("[EE_AI_CALL_ERROR]", error);
        console.error("[EE_CORRECTION_ERROR_WITH_ORIGINAL_TEXT]", error);
        setWritingCorrectionError(error.message || "Não foi possível concluir a correção IA agora.");
      } finally {
        console.log("[EE_AI_CALL_FINALLY_CLEAR_LOADING]");
        setIsCorrectingWriting(false);
      }
  }, [shouldRunWritingAI, writingTasks, displayOriginalTextValue, state.section, id, historyRecord, examResult, state.historyId]);

  useEffect(() => {
    if (!shouldRunWritingAI) return;
    if (hasStartedWritingCorrectionRef.current) return;
    if (correctionResult) return;

    console.log("[EE_AUTO_CORRECTION_EFFECT_WILL_RUN]");

    hasStartedWritingCorrectionRef.current = true;
    runWritingCorrection();
  }, [shouldRunWritingAI, runWritingCorrection, correctionResult]);

  useEffect(() => {
    if (!isCorrectingWriting) return;

    console.log("[EE_LOADING_WATCHDOG_STARTED]");

    const timer = window.setTimeout(() => {
      console.error("[EE_LOADING_WATCHDOG_TIMEOUT] Correção travada por mais de 75 segundos.");

      setIsCorrectingWriting(false);
      setWritingCorrectionError(
        "A correção demorou mais que o esperado. Tente corrigir novamente."
      );
    }, 75000);

    return () => {
      window.clearTimeout(timer);
      console.log("[EE_LOADING_WATCHDOG_CLEARED]");
    };
  }, [isCorrectingWriting]);

  const retryWritingCorrection = useCallback(async () => {
    setWritingCorrectionError(null);
    setCorrectionResult(null);
    setHasAttemptedWritingCorrection(false);
    hasStartedWritingCorrectionRef.current = false;
    await runWritingCorrection();
  }, [runWritingCorrection]);

  // Writing specific parameters
  const originalText =
    examResult?.metadata?.fullOriginalText ||
    examResult?.metadata?.originalWritingText ||
    examResult?.metadata?.userText ||
    examResult?.metadata?.originalAnswer ||
    examResult?.text ||
    writingCorrection?.originalText ||
    writingCorrection?.userText ||
    "";

  const taskTexts = (examResult?.metadata?.writingTasks || [])
    .map((task: any) => task.userText || task.answer || task.text || "")
    .filter(Boolean);

  const displayOriginalText =
    originalText || taskTexts.join("\n\n") || decodeURIComponent(searchParams.get("text") || "");

  const words =
    examResult?.metadata?.wordCount ||
    examResult?.words ||
    displayOriginalText.trim().split(/\s+/).filter(Boolean).length;

  const timeSpent = (examResult as any)?.timeUsed || parseInt(searchParams.get("time") || "0");
  const text = displayOriginalText;
  const textToCorrect = displayOriginalText;

  console.log("[TEST_RESULT_EE_ORIGINAL_TEXT_RESOLVED]", {
    displayOriginalTextLength: displayOriginalText.length,
    wordCount: words,
    source: originalText ? "metadata/text" : (taskTexts.length > 0 ? "writingTasks" : "url/fallback"),
  });
  const goalToUse = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.writing;

  const correctionToShow =
    correctionResult ||
    examResult?.writingCorrection ||
    examResult?.aiCorrection ||
    examResult?.correction ||
    examResult?.result?.ee?.correction ||
    examResult?.result?.writingCorrection ||
    examResult?.result?.correction ||
    examResult?.sections?.writing?.correction ||
    examResult?.answers?.sectionA?.correction ||
    examResult?.answers?.sectionB?.correction;

  // Granular production access
  const eeAnswers = (examResult as any)?.answers || (examResult as any)?.result?.ee?.answers || (examResult as any)?.result?.answers || {};
  const hasSectionA = !!eeAnswers.sectionA?.answer;
  const hasSectionB = !!eeAnswers.sectionB?.answer;
  const isMultiTask = hasSectionA && hasSectionB;

  const formatSpentTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (mode === "full_exam") {
    const readingResult = getSkillResult(examResult, "reading");
    const listeningResult = getSkillResult(examResult, "listening");
    const writingResult = getSkillResult(examResult, "writing");
    const readingAnswerReview = getAnswerReview(examResult, state, "reading");
    const listeningAnswerReview = getAnswerReview(examResult, state, "listening");

    const reading = readingResult || { correct: 0, total: 0, percent: 0, nclc: "<3", cefr: "A1" };
    const listening = listeningResult || { correct: 0, total: 0, percent: 0, nclc: "<3", cefr: "A1" };
    const normalizedEE = normalizeWritingCorrection(correctionToShow);

    const writing = { 
      correct: normalizedEE?.score || writingResult?.correct || 0, 
      total: normalizedEE?.maxScore || 450, 
      percent: normalizedEE ? Math.round((normalizedEE.score / normalizedEE.maxScore) * 100) : (writingResult?.percent || 0), 
      nclc: normalizedEE?.nclcLevel || writingResult?.nclc || null, 
      cefr: normalizedEE?.cefrLevel || writingResult?.cefr || null, 
      text: writingResult?.text || textToCorrect || "", 
      words: writingResult?.words || words || 0, 
      status: normalizedEE ? "corrected" : (writingResult?.status || "pending"), 
      correction: normalizedEE || writingResult?.correction || null,
      answerReview: []
    };
    const timeSpentString = examResult?.timeSpent || "N/A";

    const readingNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.reading;
    const listeningNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.listening;
    const writingNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.writing;

    const readingTefScore = parseFloat(getTefScore("reading", reading.correct, reading.total)) || 0;
    const readingGap = getScoreGapToGoal("reading", readingTefScore, readingNclcGoal);

    const listeningTefScore = parseFloat(getTefScore("listening", listening.correct, listening.total)) || 0;
    const listeningGap = getScoreGapToGoal("listening", listeningTefScore, listeningNclcGoal);

    const writingTargetScore = getTargetScoreForNclc("writing", writingNclcGoal);

    return (
      <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500 pb-12">
        {localToast && (
          <div className="fixed top-6 right-6 z-[100] bg-gray-950 text-white font-bold text-xs py-3 px-5 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-top-4 duration-300">
            <div className="w-2 h-2 rounded-full bg-purple-400 animate-ping" />
            <span>{localToast}</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("testResult.back")}
        </Button>
      {searchParams.get("dbError") === "true" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-center gap-3 text-xs font-bold shadow-sm animate-in fade-in duration-300">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <span>{t("testResult.dbErrorAlert")}</span>
        </div>
      )}

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-gray-900">{t("testResult.completeTestResultTitle")}</h1>
            <p className="text-gray-500 mt-1">{t("testResult.completeTestResultDesc")}</p>
          </div>
          <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-100 rounded-xl font-mono text-xs text-gray-700 font-bold shadow-sm">
            <Clock className="w-4 h-4 text-gray-500" />
            <span>{t("testResult.totalTime")} {timeSpentString}</span>
          </div>
        </div>

        {/* Triple Column Competence Overview Grid */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* Reading Card */}
          <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col justify-between group hover:shadow-md transition-all duration-300">
            <div>
              <div className="p-1 bg-gradient-to-r from-purple-500 to-indigo-600" />
              <CardHeader className="pt-6 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">{getCompetencyLabel("reading")}</span>
                  <Badge className="bg-purple-50 text-purple-700 border-none hover:bg-purple-100 text-[10px] font-bold">
                    {formatPercentage(reading.percent)}
                  </Badge>
                </div>
                <CardTitle className="text-lg font-bold mt-2">Compréhension écrite</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 py-4">
                {reading.total === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-center text-xs font-bold shadow-sm flex flex-col items-center justify-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                    <span>{t("testResult.cannotIdentifyQuestions")}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-center py-4 bg-gray-50 rounded-xl space-y-1">
                      <div className="text-3xl font-black text-gray-900 font-mono">
                        {getTefScore("reading", reading.correct, reading.total, 699)}
                        <span className="text-xs text-gray-400 font-bold"> / 699 pts</span>
                      </div>
                      <div className="flex justify-center gap-2 pt-1">
                        <Badge variant="outline" className="text-[10px] font-bold border-purple-100 bg-purple-50 text-purple-700">
                          NCLC {reading.nclc || getNCLC("reading", reading.correct, reading.total)}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] font-bold border-indigo-100 bg-indigo-50 text-indigo-700">
                          {reading.cefr || getCECRL("reading", reading.correct, reading.total)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-center text-gray-500 font-semibold">
                      {t("testResult.questionsCorrect", { correct: reading.correct, total: reading.total })}
                    </div>
                    <Progress value={reading.percent} className="h-1.5" />
                    <div className="pt-2.5 border-t border-slate-100 text-xs font-semibold">
                      {readingGap.achieved ? (
                        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700 space-y-0.5">
                          <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {readingNclcGoal}</p>
                          <p className="text-[10px] text-emerald-500 font-normal">{t("testResult.scoreObtained")} {readingTefScore} ({t("testResult.meta")}: {readingGap.targetScore})</p>
                          <p className="text-emerald-700 font-bold text-[11px] mt-1">✓ {t("testResult.scoreGoalReached")}</p>
                        </div>
                      ) : (
                        <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-700 space-y-0.5">
                          <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {readingNclcGoal}</p>
                          <p className="text-[10px] text-emerald-500 font-normal">{t("testResult.scoreObtained")} {readingTefScore} ({t("testResult.meta")}: {readingGap.targetScore})</p>
                          <p className="text-amber-700 font-bold text-[11px] mt-1">{t("testResult.scoreGoalGapPoints", { count: readingGap.missingPoints })}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </div>
            <CardFooter className="bg-gray-50/50 py-3 text-center text-xs font-semibold text-gray-500 justify-center">
              {getPerformanceMessage(reading.correct, reading.total, t)}
            </CardFooter>
          </Card>
 
          {/* Listening Card */}
          <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col justify-between group hover:shadow-md transition-all duration-300">
            <div>
              <div className="p-1 bg-gradient-to-r from-blue-500 to-sky-600" />
              <CardHeader className="pt-6 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{getCompetencyLabel("listening")}</span>
                  <Badge className="bg-blue-50 text-blue-700 border-none hover:bg-blue-100 text-[10px] font-bold">
                    {formatPercentage(listening.percent)}
                  </Badge>
                </div>
                <CardTitle className="text-lg font-bold mt-2">Compréhension orale</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 py-4">
                {listening.total === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-center text-xs font-bold shadow-sm flex flex-col items-center justify-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                    <span>{t("testResult.cannotIdentifyQuestions")}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-center py-4 bg-gray-50 rounded-xl space-y-1">
                      <div className="text-3xl font-black text-gray-900 font-mono">
                        {getTefScore("listening", listening.correct, listening.total, 699)}
                        <span className="text-xs text-gray-400 font-bold"> / 699 pts</span>
                      </div>
                      <div className="flex justify-center gap-2 pt-1">
                        <Badge variant="outline" className="text-[10px] font-bold border-blue-100 bg-blue-50 text-blue-700">
                          NCLC {listening.nclc || getNCLC("listening", listening.correct, listening.total)}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] font-bold border-sky-100 bg-sky-50 text-sky-700">
                          {listening.cefr || getCECRL("listening", listening.correct, listening.total)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-center text-gray-500 font-semibold">
                      {t("testResult.questionsCorrect", { correct: listening.correct, total: listening.total })}
                    </div>
                    <Progress value={listening.percent} className="h-1.5" />
                    <div className="pt-2.5 border-t border-slate-100 text-xs font-semibold">
                      {listeningGap.achieved ? (
                        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700 space-y-0.5">
                          <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {listeningNclcGoal}</p>
                          <p className="text-[10px] text-emerald-500 font-normal">{t("testResult.scoreObtained")} {listeningTefScore} ({t("testResult.meta")}: {listeningGap.targetScore})</p>
                          <p className="text-emerald-700 font-bold text-[11px] mt-1">✓ {t("testResult.scoreGoalReached")}</p>
                        </div>
                      ) : (
                        <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-700 space-y-0.5">
                          <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {listeningNclcGoal}</p>
                          <p className="text-[10px] text-emerald-500 font-normal">{t("testResult.scoreObtained")} {listeningTefScore} ({t("testResult.meta")}: {listeningGap.targetScore})</p>
                          <p className="text-amber-700 font-bold text-[11px] mt-1">{t("testResult.scoreGoalGapPoints", { count: listeningGap.missingPoints })}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </div>
            <CardFooter className="bg-gray-50/50 py-3 text-center text-xs font-semibold text-gray-500 justify-center">
              {getPerformanceMessage(listening.correct, listening.total, t)}
            </CardFooter>
          </Card>
 
          {/* Writing Card */}
          <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col justify-between group hover:shadow-md transition-all duration-300">
            <div>
              <div className={`p-1 bg-gradient-to-r ${correctionResult ? 'from-purple-500 to-indigo-600' : 'from-emerald-500 to-teal-600'}`} />
              <CardHeader className="pt-6 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">{getCompetencyLabel("writing")}</span>
                  {correctionResult ? (
                    <Badge className="bg-purple-100 text-purple-700 border-none hover:bg-purple-200 text-[10px] font-bold">
                      {t("testResult.correctedByAI")}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-none hover:bg-amber-100 text-[10px] font-bold">
                      {t("testResult.pending")}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg font-bold mt-2">Expression écrite</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 py-4">
                <div className="text-center py-6 bg-slate-50 rounded-xl space-y-2">
                  <PenTool className="w-8 h-8 text-emerald-600 mx-auto opacity-80" />
                  <div className="text-xs text-gray-500 font-semibold">
                    {t("testResult.wordsProduced", { count: writing.words })}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    {correctionResult ? t("testResult.correctedByAI") : t("testResult.waitingAICorrection")}
                  </div>
                </div>
                <div className="pt-2.5 border-t border-slate-100 text-xs font-semibold text-left">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-slate-700 space-y-0.5">
                    <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {writingNclcGoal}</p>
                    {correctionResult ? (
                      <p className="text-emerald-700 font-bold text-[10px] uppercase tracking-wider mt-1">✓ {t("testResult.correctedByAI")}</p>
                    ) : (
                      <p className="text-amber-600 font-bold text-[10px] uppercase tracking-wider mt-1">{t("testResult.writingTaskCorrectionPending")}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </div>
            <CardFooter className="bg-gray-50/50 py-3 text-center text-xs font-semibold text-gray-500 justify-center">
              {correctionResult ? t("testResult.aiEvaluationAvailable") : t("testResult.savedInHistory")}
            </CardFooter>
          </Card>
        </div>

        {/* Written Essay Display / Correction Section */}
        {isCorrecting ? (
          <WritingCorrectionLoading />
        ) : isMultiTask ? (
          <div className="space-y-6">
            {[
              { id: 'sectionA', label: 'Section A', data: eeAnswers.sectionA },
              { id: 'sectionB', label: 'Section B', data: eeAnswers.sectionB }
            ].map((task) => (
              <div key={task.id} className="space-y-4">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1.5 h-4 bg-purple-500 rounded-full" />
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{task.label}</h3>
                </div>
                
                {task.data?.correction ? (
                  <WritingCorrectionReport 
                    correction={task.data.correction} 
                    showToast={showLocalToast} 
                    historyId={id || historyRecord?.id || examResult?.id || state?.historyId}
                    originalWritingText={task.data.answer}
                    onRegenerate={() => {
                      handleStartAiCorrection(task.data.answer, writingNclcGoal, task.label, task.data.instruction);
                    }} 
                  />
                ) : (
                  <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
                    <CardHeader className="py-4 border-b border-gray-50 flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold text-slate-800">{t("testResult.yourProduction")} - {task.label}</CardTitle>
                        <CardDescription className="text-xs">{t("testResult.originallySubmittedText")}</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs font-mono font-bold bg-slate-50 text-slate-600">
                          {t("testResult.wordsCount", { count: task.data?.wordCount || 0 })}
                        </Badge>
                        {task.data?.answer && (
                          <Button 
                            onClick={() => handleStartAiCorrection(task.data.answer, writingNclcGoal, task.label, task.data.instruction)}
                            disabled={isCorrecting}
                            className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs h-8 px-3 flex items-center gap-1 shadow-sm"
                          >
                            <Sparkles className="w-3 h-3" />
                            {t("testResult.correctTask", { task: task.label })}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm">
                        {task.data?.answer || t("testResult.noProductionForTask")}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {correctionResult ? (
              <WritingCorrectionReport 
                correction={correctionResult} 
                showToast={showLocalToast} 
                historyId={id || historyRecord?.id || examResult?.id || state?.historyId}
                originalWritingText={text || writing.text}
                onRegenerate={() => {
                  handleStartAiCorrection(
                    textToCorrect || writing.text, 
                    goalToUse, 
                    correctionResult.section || "Section B", 
                    correctionResult.instruction || "Sujet de rédaction d'Expression Écrite TEF Canada."
                  );
                }} 
              />
            ) : (
              <Card className="border-none shadow-sm bg-white">
                <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-bold">{t("testResult.essayProducedInMock")}</CardTitle>
                    <CardDescription>{t("testResult.officialAnswerSheetEE")}</CardDescription>
                  </div>
                  {writing.text && (
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Button 
                        onClick={() => handleStartAiCorrection(writing.text, writingNclcGoal, "Section B", "Sujet de rédaction d'Expression Écrite TEF Canada.")}
                        disabled={isCorrecting || (usageInfo !== null && usageInfo.remaining === 0)}
                        className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs h-9 px-4 flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isCorrecting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t("common.loading")}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" />
                            {t("testResult.correctWithAI")}
                          </>
                        )}
                      </Button>
                      <span className="text-[10px] text-gray-400 font-bold tracking-tight">
                        {usageInfo ? t("testResult.remainingCorrectionsToday", { count: usageInfo.remaining }) : t("testResult.limitUnavailable")}
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm max-h-[350px] overflow-y-auto">
                    {writing.text || t("testResult.noTextProducedWriting")}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Collapsible original redaction box */}
            {correctionResult && (
              <Card className="border-none shadow-sm bg-white rounded-2xl">
                <CardHeader className="py-4 border-b border-gray-50 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-bold text-slate-800">{t("testResult.yourOriginalEssay")}</CardTitle>
                    <CardDescription className="text-xs">{t("testResult.textOriginallySubmittedEE")}</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono font-bold bg-slate-50 text-slate-600">
                    {t("testResult.wordsCount", { count: writing.words || 0 })}
                  </Badge>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm max-h-[350px] overflow-y-auto">
                    {writing.text || t("testResult.noTextProducedWriting")}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Answer Review sections for Full Exam */}
        <div className="space-y-6 pt-4">
          <PedagogicalAnalysisPanel 
            reviewItems={readingAnswerReview} 
            title={t("testResult.pedagogicalAnalysisCE")}
          />
          <AnswerReviewPanel 
            reviewItems={readingAnswerReview} 
            title={t("testResult.answerReviewCE")} 
            description={t("testResult.answerReviewCEDesc")} 
            compType="reading"
            savedErrorIds={savedErrorIds}
            createdFlashcardIds={createdFlashcardIds}
            savingErrorId={savingErrorId}
            creatingFlashcardId={creatingFlashcardId}
            onSaveError={handleSaveError}
            onCreateFlashcard={handleCreateFlashcard}
          />
          <PedagogicalAnalysisPanel 
            reviewItems={listeningAnswerReview} 
            title={t("testResult.pedagogicalAnalysisCO")}
          />
          <AnswerReviewPanel 
            reviewItems={listeningAnswerReview} 
            title={t("testResult.answerReviewCO")} 
            description={t("testResult.answerReviewCODesc")} 
            compType="listening"
            savedErrorIds={savedErrorIds}
            createdFlashcardIds={createdFlashcardIds}
            savingErrorId={savingErrorId}
            creatingFlashcardId={creatingFlashcardId}
            onSaveError={handleSaveError}
            onCreateFlashcard={handleCreateFlashcard}
          />
        </div>

        {/* Control Actions Row */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button asChild variant="outline" className="rounded-xl flex-1 bg-white border-gray-100 text-gray-600 hover:bg-gray-50">
            <Link to="/start-test">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("testResult.tryAgain")}
            </Link>
          </Button>
          <Button asChild className="rounded-xl flex-1 bg-primary hover:bg-primary/90 text-white shadow-md">
            <Link to="/dashboard">
              {t("testResult.goToDashboardButton")}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Legacy single competency test result
  const singleNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : (type === "reading" ? userGoals.goals.reading : (type === "listening" ? userGoals.goals.listening : userGoals.goals.writing));
  const singleTefScore = parseFloat(getTefScore(type, score, total)) || 0;
  const singleGap = getScoreGapToGoal(type, singleTefScore, singleNclcGoal);
  const singleTargetScore = getTargetScoreForNclc(type, singleNclcGoal);
  console.log("[EE_RENDER_STATE]", {
    isWritingResult: isWritingReview,
    shouldRunWritingAI,
    isCorrectingWriting,
    hasAttemptedWritingCorrection,
    hasWritingCorrection: !!correctionResult,
    writingCorrectionError,
  });

  const isEE = isWritingEE(state) || isWritingEE(historyRecord);
  const isV2Placeholder = 
    state?.resultType === "writing_v2_placeholder" || 
    state?.resultType === "writing_v2" || 
    isWritingV2Result(state) || 
    isWritingV2Result(historyRecord);

  console.log("[EE_V2_RENDER_MODE]", {
    resultType: state?.resultType || historyRecord?.resultType,
    writingVersion: state?.writingVersion || historyRecord?.metadata?.writingVersion || historyRecord?.writingVersion,
    fromHistory: Boolean(historyRecord),
    isWritingV2: isV2Placeholder,
    usingLegacyScore: false,
  });

  if (isEE && isV2Placeholder) {
    const resolvedTasks = resolveWritingV2TasksFromState(state, examResult || historyRecord);
    
    console.log("[EE_V2_TEST_RESULT_TASKS_RESOLVED]", {
      state: state,
      tasks: resolvedTasks.map((t: any) => ({
        id: t.id,
        section: t.section,
        title: t.title,
        userTextLength: t.userText?.length || 0,
        wordCount: t.wordCount,
      })),
    });

    const writingTasks = resolvedTasks;
    const hasTasks = writingTasks.length > 0;

    const writingTask = state?.writingTask;
    const writingCorrection = state?.writingCorrection;

    console.log("[EE_V2_HISTORY_REVIEW_RENDER]", {
      hasHistoryRecord: Boolean(historyRecord),
      historyId: historyRecord?.id,
      hasTask: Boolean(writingTask),
      hasCorrection: Boolean(writingCorrection),
      userTextLength: writingTask?.userText?.length || 0,
    });

    const hasAnyText = writingTasks.some((task: any) =>
      String(task.userText || "").trim().length > 0
    );

    console.log("[EE_V2_TEST_RESULT_HAS_TEXT]", { hasAnyText });

    return (
      <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("testResult.goToDashboardButton")}
        </Button>

        <Card className="border-2 border-purple-100 shadow-sm overflow-hidden bg-white/70 backdrop-blur-sm rounded-xl">
          <CardHeader className="bg-gradient-to-br from-purple-50 to-white border-b border-purple-100/50 p-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1 bg-purple-100 text-purple-600 rounded-md">
                <Sparkles className="w-4 h-4" />
              </div>
              <span className="font-semibold text-purple-600 text-xs uppercase tracking-wider">
                {t("testResult.pedagogicalCorrection")}
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {t("testResult.resultEE")}
            </CardTitle>
            <CardDescription className="text-gray-600 text-sm font-medium">
              {t("testResult.pedagogicalCorrectionDesc")}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6 space-y-6">
            {!hasAnyText ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">{t("testResult.noTextSent")}</h3>
                <p className="text-gray-500 text-xs mt-1">{t("testResult.noTextSentDesc")}</p>
              </div>
            ) : (
              <>
                {state.testType !== "writing_v2_complete" && writingTasks.length !== 1 && (
                  writingTasks.map((task: any, index: number) => (
                    <div key={task.id || index} className="p-4 bg-gray-50/50 border border-gray-100 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {task.section || `Tarefa ${index + 1}`}
                        </h3>
                        <Badge variant={task.meetsMinimumWordCount ? "default" : "secondary"}>
                          {task.wordCount} / {t("testResult.wordsCount", { count: task.minimumWordsRequired })}
                        </Badge>
                      </div>
                      
                      {task.title && (
                        <p className="text-gray-700 text-xs font-semibold">{task.title}</p>
                      )}
                      
                      {task.prompt && (
                        <div className="text-gray-600 text-xs bg-white p-3 rounded border border-gray-100">
                          <p className="font-medium mb-1">{t("testResult.instructionLabel")}</p>
                          <div dangerouslySetInnerHTML={{ __html: task.prompt }} />
                        </div>
                      )}
                      
                      <div className="bg-white p-4 rounded border border-gray-100">
                        <p className="text-gray-500 text-xs font-medium mb-2">{t("testResult.yourRedactionLabel")}</p>
                        <p className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed">
                          {task.userText || t("testResult.noTextProduced")}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1.5 text-xs">
                        {task.meetsMinimumWordCount ? (
                          <div className="flex items-center gap-1 text-green-600 font-medium">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{t("testResult.meetsMinWordCount")}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-amber-600 font-medium">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{t("testResult.doesNotMeetMinWordCount", { count: task.minimumWordsRequired })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
            
            {/* Modo Teste Completo EE v2 */}
            {hasTasks && state.testType === "writing_v2_complete" && (
              <div className="space-y-8 w-full">
                {console.log("[EE_V2_COMPLETE_RESULT_RENDER]", { writingTasks })}
                {console.log("[EE_V2_COMPLETE_RESULT_TASKS]", writingTasks.map((t: any) => ({
                  id: t.id,
                  section: t.section,
                  userTextLength: t.userText?.length || 0,
                  wordCount: t.wordCount,
                  minimumWordsRequired: t.minimumWordsRequired,
                })))}
                {console.log("[EE_V2_COMPLETE_CORRECTIONS_STATE]", {
                  hasSectionA: Boolean(writingV2CorrectionsBySection["Section A"]),
                  hasSectionB: Boolean(writingV2CorrectionsBySection["Section B"]),
                })}
                {writingTasks.map((task: any, idx: number) => {
                  const section = task.section || (idx === 0 ? "Section A" : "Section B");
                  const correction = writingV2CorrectionsBySection[section];
                  const isCorrecting = isCorrectingBySection[section];
                  const error = writingV2ErrorsBySection[section];

                  return (
                    <div key={section} className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
                      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-600">{section}</Badge>
                          <h3 className="text-base font-bold text-gray-900">{task.title || "Redação"}</h3>
                        </div>
                        <span className="text-xs text-gray-500">{t("testResult.wordsCount", { count: task.wordCount })}</span>
                      </div>

                      <div className="bg-gray-50 p-4 rounded border border-gray-100">
                        <p className="text-gray-500 text-xs font-medium mb-2">{t("testResult.yourRedactionLabel")}</p>
                        <p className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed">
                          {task.userText || t("testResult.noTextProduced")}
                        </p>
                      </div>

                      {!correction && !error && (
                        <div className="flex justify-end">
                          <Button
                            onClick={() => handleCorrectWritingV2BySection(task, section)}
                            disabled={isCorrecting}
                            className="text-xs bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            {isCorrecting ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                                {t("common.loading")}
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3.5 h-3.5 mr-1" />
                                {t("testResult.correctWithAIPedagogical")}
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {error && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-lg flex gap-2 items-start">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                          <div>
                            <p className="text-red-700 text-sm font-bold">{t("testResult.correctionError")}</p>
                            <p className="text-red-600 text-xs">{error}</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                console.log("[EE_V2_RETRY_CORRECTION_CLICK]", {
                                  section: task.section,
                                  userTextLength: task.userText?.length || 0,
                                  wordCount: task.wordCount,
                                });
                                handleCorrectWritingV2BySection(task, section);
                              }}
                              className="mt-2 text-xs border-red-200 text-red-700 hover:bg-red-50"
                            >
                              {t("testResult.tryAgain")}
                            </Button>
                          </div>
                        </div>
                      )}

                      {correction && (
                        <WritingV2CorrectionPanel
                          task={task}
                          correction={correction}
                          historyRecordId={savedRecordsBySection[section]?.id || historyRecord?.id || null}
                          fromHistory={false}
                          testMode="complete"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Modo Individual (Mantido para compatibilidade) */}
            {hasTasks && state.testType !== "writing_v2_complete" && writingTasks.length === 1 && (writingTasks[0].section === "Section A" || writingTasks[0].section === "Section B") && (
              <div className="w-full">
                {(() => {
                  const task = writingTasks[0];
                  const section = task.section;
                  const correction = writingV2CorrectionsBySection[section] || writingCorrection;
                  const isCorrecting = isCorrectingBySection[section];
                  const error = writingV2ErrorsBySection[section];

                  if (!correction && !error) {
                    return (
                      <div className="p-6 bg-white border border-purple-100 rounded-lg shadow-sm space-y-4 text-center">
                        {isCorrecting ? (
                          <>
                            <div className="flex justify-center">
                              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">{t("testResult.correctingCompositionWithAI")}</h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {section === "Section B"
                                  ? t("testResult.analyzingGrammarCO")
                                  : t("testResult.analyzingGrammarCE")}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-center">
                              <Sparkles className="w-8 h-8 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">{t("testResult.pedagogicalCorrectionWithAI")}</h3>
                              <p className="text-xs text-gray-500 mt-1 mb-4">
                                {t("testResult.pedagogicalCorrectionWithAIDesc")}
                              </p>
                              <Button
                                onClick={() => handleCorrectWritingV2BySection(task, section)}
                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                              >
                                {t("testResult.correctWithAIPedagogical")}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }

                  if (error) {
                    return (
                      <div className="p-4 bg-red-50 border border-red-100 rounded-lg space-y-3">
                        <div className="flex items-center gap-2 text-red-700 font-bold text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span>{t("testResult.correctionError")}:</span>
                        </div>
                        <p className="text-xs text-red-600">{error}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            console.log("[EE_V2_RETRY_CORRECTION_CLICK]", {
                              section: task.section,
                              userTextLength: task.userText?.length || 0,
                              wordCount: task.wordCount,
                            });
                            handleCorrectWritingV2BySection(task, section);
                          }}
                          className="text-xs border-red-200 text-red-700 hover:bg-red-50"
                        >
                          {t("testResult.tryAgain")}
                        </Button>
                      </div>
                    );
                  }

                  return correction ? (
                    <WritingV2CorrectionPanel
                      task={task}
                      correction={correction}
                      historyRecordId={savedRecordsBySection[section]?.id || historyRecord?.id || null}
                      fromHistory={false}
                      testMode="individual"
                    />
                  ) : null;
                })()}
              </div>
            )}
          </CardContent>

          <CardFooter className="bg-gray-50/50 border-t border-gray-100 p-6 flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1.5 font-semibold text-xs text-gray-700 hover:bg-white rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("testResult.back")}
            </Button>
            
            <Button
              size="sm"
              onClick={() => navigate("/")}
              className="gap-1.5 font-semibold text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm"
            >
              {t("testResult.goToDashboardButton")}
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500 pb-12">
      {localToast && (
        <div className="fixed top-6 right-6 z-[100] bg-gray-950 text-white font-bold text-xs py-3 px-5 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-top-4 duration-300">
          <div className="w-2 h-2 rounded-full bg-purple-400 animate-ping" />
          <span>{localToast}</span>
        </div>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("testResult.back")}
      </Button>
      {searchParams.get("dbError") === "true" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-center gap-3 text-xs font-bold shadow-sm animate-in fade-in duration-300">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <span>{t("testResult.dbErrorAlert")}</span>
        </div>
      )}


      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("testResult.individualTestResultTitle")}</h1>
        <p className="text-gray-500 mt-1">{t("testResult.individualTestResultDesc")}</p>
      </div>

      {isWritingReview ? (
        /* Writing Layout */
        <div className="space-y-6">
          {isCorrecting || isCorrectingWriting ? (
            <WritingCorrectionLoading />
          ) : writingCorrectionError && !writingCorrection ? (
            <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-2xl flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-900">{t("testResult.cannotConcludeAICorrection")}</h3>
                <p className="text-sm text-red-700 mt-1">{writingCorrectionError}</p>
              </div>
              <div className="flex gap-3 mt-2">
                <button 
                  onClick={retryWritingCorrection} 
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.586m15.414 2A8 8 0 118 4.07M12 2v2" />
                  </svg>
                  {t("testResult.retryCorrectionButton")}
                </button>
                <button 
                  onClick={() => navigate("/dashboard")} 
                  className="bg-white hover:bg-gray-50 text-gray-700 font-bold py-2 px-4 rounded-xl border border-gray-200 transition-colors"
                >
                  {t("testResult.goToDashboardButton")}
                </button>
              </div>
            </div>
          ) : (isWritingFromHistory || writingCorrection !== null || writingScore !== null) ? (
            <div className="space-y-6">
              {writingCorrection ? (
                <WritingCorrectionReport 
                  correction={writingCorrection} 
                  showToast={showLocalToast} 
                  historyId={id || historyRecord?.id || examResult?.id || state?.historyId}
                  originalWritingText={text}
                  onRegenerate={() => {
                    handleStartAiCorrection(
                      textToCorrect, 
                      goalToUse, 
                      writingCorrection.section || "Section B", 
                      writingCorrection.instruction || "Sujet de rédaction d'Expression Écrite TEF Canada."
                    );
                  }} 
                />
              ) : (
                <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl border border-red-100">
                  <CardContent className="p-6">
                    {(isFromHistory && writingScore > 0 && !hasWritingCorrection) ? (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-center gap-3 text-xs font-bold shadow-sm">
                        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                        <span>{t("testResult.onlyScoreSavedWarning")}</span>
                      </div>
                    ) : !isFromHistory ? (
                      <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-2xl flex flex-col items-center gap-4 text-center">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-red-900">{t("testResult.cannotConcludeAICorrection")}</h3>
                          <p className="text-sm text-red-700 mt-1">{t("testResult.aiFailedOrInvalidCommunication")}</p>
                        </div>
                        <div className="flex gap-3 mt-2">
                          <button 
                            onClick={retryWritingCorrection} 
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.586m15.414 2A8 8 0 118 4.07M12 2v2" />
                            </svg>
                            {t("testResult.retryCorrectionButton")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl flex items-center gap-3 text-xs font-bold shadow-sm">
                        <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                        <span>{t("testResult.aiCorrectionNotFoundForTest")}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="border-none shadow-sm bg-white rounded-2xl">
                <CardHeader className="py-4 border-b border-gray-50 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-bold text-slate-800">{t("testResult.yourOriginalEssay")}</CardTitle>
                    <CardDescription className="text-xs">{t("testResult.textOriginallySubmittedEE")}</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono font-bold bg-slate-50 text-slate-600">
                    {t("testResult.wordsCount", { count: words || 0 })}
                  </Badge>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm max-h-[350px] overflow-y-auto">
                    {text || (isWritingReview ? t("testResult.originalTextNotFound") : t("testResult.noTextProduced"))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : correctionResult ? (
            <div className="space-y-6">
              <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl border border-purple-100">
                <div className="p-1.5 bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600" />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <CardTitle className="text-base font-bold text-gray-900">{t("testResult.aiCorrectionCompleted")}</CardTitle>
                    </div>
                    <Badge className="bg-purple-50 text-purple-700 border-none hover:bg-purple-100 text-[11px] font-extrabold">
                      {t("testResult.evaluationOrigin")} {correctionSource === "openai" || correctionSource === "gemini" ? `${t("testResult.realAICorrection")} (${correctionSource === "gemini" ? "Gemini" : "OpenAI"})` : t("testResult.simulatedCorrection")}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-2 gap-4 bg-purple-50/40 p-4 rounded-xl border border-purple-50/50">
                    <div className="space-y-0.5">
                      <span className="text-[10px] text-purple-700 uppercase font-black tracking-wider">{t("testResult.estimatedScoreTitle")}</span>
                      <div className="text-2xl font-black text-purple-950 font-mono">
                        {correctionResult.score || correctionResult.estimatedScore || 0}
                        <span className="text-xs text-purple-400 font-bold"> / {correctionResult.maxScore || 450} pts</span>
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[10px] text-purple-700 uppercase font-black tracking-wider">{t("testResult.estimatedLevelTitle")}</span>
                      <div className="text-base font-bold text-purple-950 flex items-center gap-1.5 pt-0.5">
                        <Badge variant="outline" className="text-[11px] font-black border-purple-200 bg-white text-purple-700">
                          {correctionResult.nclc || correctionResult.nclcLevel || correctionResult.nclcEstimate || "N/A"}
                        </Badge>
                        <Badge variant="outline" className="text-[11px] font-black border-indigo-200 bg-white text-indigo-700">
                          {correctionResult.cefr || correctionResult.cefrLevel || correctionResult.cecrlEstimate || "N/A"}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{t("testResult.summaryGeneralFeedback")}</h4>
                    <p className="text-xs text-gray-600 font-semibold leading-relaxed bg-slate-50 p-3.5 rounded-xl border border-slate-100 font-serif">
                      {correctionResult.overallComment || correctionResult.generalFeedback || t("testResult.noGeneralFeedback")}
                    </p>
                  </div>

                  {/* 3 to 5 Key Improvements */}
                  {((correctionResult.weaknesses && correctionResult.weaknesses.length > 0) || 
                    (correctionResult.studySuggestions && correctionResult.studySuggestions.length > 0)) && (
                    <div className="space-y-1.5">
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{t("testResult.keyPointsImprovement")}</h4>
                      <ul className="text-xs text-gray-600 space-y-1 pl-4 list-disc font-semibold">
                        {(correctionResult.weaknesses || correctionResult.studySuggestions || [])
                          .slice(0, 5)
                          .map((item: string, idx: number) => (
                            <li key={idx} className="leading-relaxed">{item}</li>
                          ))
                        }
                      </ul>
                    </div>
                  )}

                  {/* Count of detected errors */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                    <span className="text-gray-500 font-bold">{t("testResult.errorsIdentified")}</span>
                    <Badge variant="outline" className={`text-xs font-bold ${correctionResult.errors?.length > 0 ? 'bg-red-50 text-red-700 border-red-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                      {correctionResult.errors ? t("testResult.errorsDetectedCount", { count: correctionResult.errors.length }) : t("testResult.noErrorsCatalogued")}
                    </Badge>
                  </div>
                </CardContent>
                <CardFooter className="bg-slate-50 border-t border-slate-100 py-3.5 justify-end">
                  {!state?.fromHistory && (
                    <Button 
                      onClick={handleManualSaveToHistory} 
                      disabled={isSavingHistory || historySaveStatus === "success"}
                      className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs h-10 px-5 shadow-md flex items-center gap-1.5 mr-2"
                    >
                      {isSavingHistory ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {t("common.saving")}
                        </>
                      ) : historySaveStatus === "success" ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {t("testResult.savedInHistoryBadge")}
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {t("testResult.saveToHistoryButton")}
                        </>
                      )}
                    </Button>
                  )}
                  <Button 
                    onClick={() => {
                      if (correctionToShow) {
                        setShowFullCorrectionModal(true);
                      } else {
                        if (state?.fromHistory) {
                          showLocalToast(t("testResult.toastNoFullReportHistory"));
                        } else {
                          showLocalToast(t("testResult.toastCorrectionNotAvailableYet"));
                        }
                      }
                    }}
                    className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs h-10 px-5 shadow-md flex items-center gap-1.5"
                  >
                    <FileText className="w-4 h-4" />
                    {t("testResult.viewCompleteCorrection")}
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Collapsible original redaction box */}
              <Card className="border-none shadow-sm bg-white rounded-2xl">
                <CardHeader className="py-4 border-b border-gray-50 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-bold text-slate-800">{t("testResult.yourOriginalEssay")}</CardTitle>
                    <CardDescription className="text-xs">{t("testResult.textOriginallySubmittedEE")}</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono font-bold bg-slate-50 text-slate-600">
                    {t("testResult.wordsCount", { count: words || 0 })}
                  </Badge>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm max-h-[350px] overflow-y-auto">
                    {text || t("testResult.noTextProduced")}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-none shadow-sm bg-white overflow-hidden">
              <div className="p-1.5 bg-emerald-500" />
              <CardHeader className="flex flex-col md:flex-row items-center gap-4 justify-between pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold">{t("testResult.writtenProductionSaved")}</CardTitle>
                    <CardDescription>{t("testResult.essayStoredSuccess")}</CardDescription>
                  </div>
                </div>
                {text && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Button 
                      onClick={() => handleStartAiCorrection(text, singleNclcGoal, "Section B", "Sujet de rédaction d'Expression Écrite TEF Canada.")}
                      disabled={isCorrecting || (usageInfo !== null && usageInfo.remaining === 0)}
                      className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs h-9 px-4 flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isCorrecting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {t("common.loading")}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5" />
                          {t("testResult.correctWithAI")}
                        </>
                      )}
                    </Button>
                    <span className="text-[10px] text-gray-400 font-bold tracking-tight">
                      {usageInfo ? t("testResult.remainingCorrectionsToday", { count: usageInfo.remaining }) : t("testResult.limitUnavailable")}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 bg-gray-50 rounded-xl space-y-1">
                    <span className="text-xs text-gray-400 font-semibold uppercase">{t("testResult.totalWords")}</span>
                    <p className="text-2xl font-bold text-gray-800 font-mono">{t("testResult.wordsCount", { count: words })}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl space-y-1">
                    <span className="text-xs text-gray-400 font-semibold uppercase">{t("testResult.productionTime")}</span>
                    <p className="text-2xl font-bold text-gray-800 font-mono">{formatSpentTime(timeSpent)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-1 text-xs flex flex-col justify-center">
                    <span className="text-[10px] text-gray-400 font-bold uppercase block">{t("testResult.proficiencyGoal")}</span>
                    <p className="font-bold text-slate-700">
                      {userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: <span className="text-primary">NCLC {singleNclcGoal}</span>
                    </p>
                    <p className="text-slate-500 font-medium">
                      {t("testResult.targetScoreEE")} <span className="font-bold">{singleTargetScore}</span>
                    </p>
                    <div className="mt-1 inline-flex self-start rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold text-amber-700">
                      {t("testResult.writingTaskCorrectionPending")}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">{t("testResult.writtenEssay")}</span>
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-serif leading-relaxed text-gray-800 whitespace-pre-wrap text-sm">
                    {text || t("testResult.noTextProduced")}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        /* Reading / Listening Layout */
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            {/* Score Card */}
            <Card className="border-none shadow-sm bg-white md:col-span-1 flex flex-col justify-between">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t("testResult.overallScoreTitle")} ({getCompetencyLabel(type)})</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center py-6">
                <div className="relative flex items-center justify-center w-36 h-36">
                  {/* SVG com barra circular verde-vermelho */}
                  <svg className="absolute w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {total <= 0 ? (
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        className="stroke-gray-100 fill-none"
                        strokeWidth="8"
                      />
                    ) : (
                      <>
                        {/* Arco Verde (Acertos) */}
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          className="stroke-emerald-500 fill-none"
                          strokeWidth="8"
                          strokeDasharray={`${correctLength} ${circumference}`}
                          strokeDashoffset="0"
                        />
                        {/* Arco Vermelho (Erros) */}
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          className="stroke-red-500 fill-none"
                          strokeWidth="8"
                          strokeDasharray={`${incorrectLength} ${circumference}`}
                          strokeDashoffset={`-${correctLength}`}
                        />
                      </>
                    )}
                  </svg>
                  <div className="text-center z-10">
                    <span className="text-3xl font-black text-gray-900">{formatPercentage(percent)}</span>
                    <span className="text-[10px] text-gray-400 font-semibold block mt-1">{t("testResult.performancePercent")}</span>
                  </div>
                </div>

                <div className="mt-4 text-center space-y-1.5 bg-gray-50 px-4 py-2.5 rounded-xl w-full">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("testResult.officialTefEquivalence")}</span>
                  <div className="text-xl font-black text-gray-800 font-mono">
                    {getTefScore(type, score, total, 699)}
                    <span className="text-xs text-gray-400 font-bold"> / 699 pts</span>
                  </div>
                  <div className="flex justify-center gap-1.5">
                    <Badge variant="outline" className="text-[9px] font-bold border-primary/10 bg-primary/5 text-primary">
                      NCLC {getNCLC(type, score, total)}
                    </Badge>
                    <Badge variant="outline" className="text-[9px] font-bold border-indigo-100 bg-indigo-50 text-indigo-700">
                      {getCECRL(type, score, total)}
                    </Badge>
                  </div>
                </div>

                <div className="mt-3.5 text-xs font-semibold w-full">
                  {singleGap.achieved ? (
                    <div className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700 space-y-0.5 text-center">
                      <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {singleNclcGoal}</p>
                      <p className="text-[9px] text-emerald-500 font-normal">{t("testResult.scoreObtained")} {singleTefScore} ({t("testResult.meta")}: {singleGap.targetScore})</p>
                      <p className="text-emerald-700 font-bold text-[11px] mt-1">✓ {t("testResult.scoreGoalReached")}</p>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-700 space-y-0.5 text-center">
                      <p>{userGoals.isCustom ? t("testResult.meta") : t("testResult.defaultMeta")}: NCLC {singleNclcGoal}</p>
                      <p className="text-[9px] text-amber-500 font-normal">{t("testResult.scoreObtained")} {singleTefScore} ({t("testResult.meta")}: {singleGap.targetScore})</p>
                      <p className="text-amber-700 font-bold text-[11px] mt-1">{t("testResult.scoreGoalGapPoints", { count: singleGap.missingPoints })}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-4 mt-4 text-center w-full border-t border-gray-100 pt-3">
                  <div className="flex-1">
                    <span className="text-xs text-gray-400 font-semibold block">{t("testResult.correctAnswers")}</span>
                    <span className="text-lg font-bold text-green-600">{score}</span>
                  </div>
                  <div className="border-r border-gray-100" />
                  <div className="flex-1">
                    <span className="text-xs text-gray-400 font-semibold block">{t("testResult.incorrectCount")}</span>
                    <span className="text-lg font-bold text-red-600">{total - score}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Diagnostic card details */}
            <PedagogicalAnalysisPanel reviewItems={singleAnswerReview} className="md:col-span-2" />
          </div>

          {/* Real Dynamic Answer Review */}
          <AnswerReviewPanel 
            reviewItems={singleAnswerReview} 
            compType={type === "reading" ? "reading" : "listening"}
            savedErrorIds={savedErrorIds}
            createdFlashcardIds={createdFlashcardIds}
            savingErrorId={savingErrorId}
            creatingFlashcardId={creatingFlashcardId}
            onSaveError={handleSaveError}
            onCreateFlashcard={handleCreateFlashcard}
          />
        </div>
      )}

      {/* Control Actions Row */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button asChild variant="outline" className="rounded-xl flex-1 bg-white border-gray-100 text-gray-600 hover:bg-gray-50">
          <Link to="/start-test">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("testResult.tryAgain")}
          </Link>
        </Button>
        <Button asChild className="rounded-xl flex-1 bg-primary hover:bg-primary/90 text-white shadow-md">
          <Link to="/dashboard">
            {t("testResult.goToDashboardButton")}
          </Link>
        </Button>
      </div>

      {/* Full Correction Report Modal Overlay */}
      {showFullCorrectionModal && correctionToShow && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100 flex flex-col animate-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-slate-50 shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-black text-gray-900">{t("testResult.fullAiReport")}</h2>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => setShowFullCorrectionModal(false)}
                className="text-gray-500 hover:text-gray-700 font-bold text-xs"
              >
                {t("common.close", "Fechar")}
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Status & Origin Header Row */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-100 rounded-2xl px-5 py-3 shadow-xs">
                <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">{t("testResult.evaluationOrigin")}</span>
                <div className="flex gap-2">
                  {correctionSource === "openai" || correctionSource === "gemini" ? (
                    <Badge className="bg-purple-600 hover:bg-purple-700 text-white font-extrabold px-3 py-1 rounded-xl text-xs flex items-center gap-1 shadow-sm transition-all border-none">
                      <Sparkles className="w-3.5 h-3.5" />
                      {t("testResult.realAICorrection")} {correctionSource === "gemini" ? "(Gemini)" : "(OpenAI)"}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-200 font-extrabold px-3 py-1 rounded-xl text-xs flex items-center gap-1 shadow-xs transition-all border">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {t("testResult.simulatedCorrection")}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Warning Banner */}
              {correctionWarning && (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3 text-amber-800 shadow-xs animate-in fade-in slide-in-from-top-2 duration-300">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-bold">{t("testResult.simulatedCorrectionActive")}</p>
                    <p className="text-xs text-amber-700 font-semibold leading-relaxed">
                      {correctionSource === "frontend_fallback" 
                        ? t("testResult.fallbackAIPedagogicalWarning")
                        : t("testResult.realAIQuotaWarning")}
                    </p>
                  </div>
                </div>
              )}

              <WritingCorrectionReport 
                correction={correctionToShow} 
                showToast={showLocalToast} 
                historyId={id || historyRecord?.id || examResult?.id || state?.historyId}
                originalWritingText={text}
                onRegenerate={() => {
                  setShowFullCorrectionModal(true);
                  handleStartAiCorrection(
                    textToCorrect, 
                    goalToUse, 
                    correctionToShow.section || "Section B", 
                    correctionToShow.instruction || "Sujet de rédaction d'Expression Écrite TEF Canada."
                  );
                }} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
